/**
 * AI Style Transfer V7.2
 *
 * State management: JS togglet `el.hidden` rechtstreeks (geen CSS state machine).
 * Dat is bulletproof — werkt zelfs als de CSS niet of laat laadt.
 *
 * Flow:
 *   1. Klik upload-CTA op tegel → file picker
 *   2. File geselecteerd → MODAL opent met crop area + frame
 *   3. User sleept image + zoomt met +/-
 *   4. "Selecteer" → modal sluit, BFL call START METEEN met progress bar
 *   5. Bij 100% → resultaat verschijnt
 *   6. Cart knop wordt enabled → klant kiest addon → toevoegen
 */

(function () {
  'use strict';
  // V67: GUARD tegen dubbele init — script wordt nu OOK globaal via
  // layout/theme.liquid geladen (voor productkaart ATC's op collection pages
  // waar de section zelf niet staat). Op productpagina's waar de section
  // ook laadt zou de listener anders DUBBEL binden → 2x cart/add call.
  if (window.__fluxV67Loaded) {
    console.log('%c[FLUX V67 skip dupe]', 'color:#fff;background:#888;padding:2px 6px;border-radius:3px');
    return;
  }
  window.__fluxV67Loaded = true;
  console.log('%c[FLUX V67 ACTIVE]', 'color:#fff;background:#E89B2E;padding:2px 6px;border-radius:3px;font-weight:bold');
  document.querySelectorAll('.flux-st').forEach(init);

  /* === Wire up "Ontwerp laten controleren" toggle in cart drawer ===
     Toggle ON  → add product 'ontwerp-laten-controleren' aan cart
     Toggle OFF → remove from cart
     Werkt global (op elke page waar deze JS geladen is) */
  const DESIGN_CHECK_VARIANT_ID = 56407268655479;

  async function syncDesignCheck(toggle) {
    if (toggle.dataset.syncing === '1') return; // voorkom dubbele triggers
    toggle.dataset.syncing = '1';
    toggle.disabled = true;
    try {
      // ALTIJD eerst cart fetchen om actuele state te weten
      const cart = await fetch('/cart.js', { cache: 'no-store' }).then(r => r.json());
      const existing = (cart.items || []).find(i => i.variant_id === DESIGN_CHECK_VARIANT_ID);
      if (toggle.checked && !existing) {
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          body: JSON.stringify({
            items: [{ id: DESIGN_CHECK_VARIANT_ID, quantity: 1 }],
            sections: getCartHeaderSectionId(),
            sections_url: window.location.pathname
          })
        });
        if (!r.ok) throw new Error('Add failed: ' + r.status);
        const data = await r.json();
        applyCartSections(data.sections);
      } else if (!toggle.checked && existing) {
        const r = await fetch('/cart/change.js', {
          method: 'POST',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json'},
          body: JSON.stringify({
            id: existing.key, quantity: 0,
            sections: getCartHeaderSectionId(),
            sections_url: window.location.pathname
          })
        });
        if (!r.ok) throw new Error('Remove failed: ' + r.status);
        const data = await r.json();
        applyCartSections(data.sections);
      }
      /* Cart events */
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (err) {
      console.error('Design check toggle error:', err);
      toggle.checked = !toggle.checked;
    } finally {
      toggle.disabled = false;
      toggle.dataset.syncing = '0';
    }
  }
  // Helper: figure out de Horizon header section ID waarin cart drawer + icon bubble zitten
  function getCartHeaderSectionId() {
    const items = document.querySelector('cart-items-component');
    if (items && items.sectionId) return items.sectionId;
    // Fallback: zoek naar header section met cart drawer erin
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer) {
      let p = drawer.parentElement;
      while (p) {
        if (p.id && p.id.startsWith('shopify-section-')) {
          return p.id.replace(/^shopify-section-/, '');
        }
        p = p.parentElement;
      }
    }
    return null;
  }
  // Helper: extract cart sections en swap de KLEINST mogelijke elementen.
  // Bewust GEEN swap van <cart-drawer-component> zelf (Horizon's interne handlers
  // breken bij replaceWith én innerHTML kan de drawer initial-render state breken).
  // We vervangen alleen <cart-items-component> + cart-bubble — die zijn safe
  // om te swappen want hun connectedCallback re-runt en rendert vers.
  function applyCartSections(sections) {
    if (!sections) return;
    const headerKey = Object.keys(sections).find(k => k.includes('header')) || Object.keys(sections)[0];
    if (!headerKey || !sections[headerKey]) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = sections[headerKey];

    // 1. Cart items component — replaceWith zodat connectedCallback re-runt
    const newItems = tmp.querySelector('cart-items-component');
    const oldItems = document.querySelector('cart-items-component');
    if (newItems && oldItems) {
      oldItems.replaceWith(newItems);
    }

    // 2. Cart bubble (count badge) — gewone span/div, safe to replace
    const newBubble = tmp.querySelector('[class*="cart-bubble"], cart-icon-bubble');
    const oldBubble = document.querySelector('[class*="cart-bubble"], cart-icon-bubble');
    if (newBubble && oldBubble) oldBubble.replaceWith(newBubble);

    // 3. Cart drawer subtotal/footer als die apart bestaat
    ['cart-totals-component', '[class*="cart-summary"]', '[class*="cart-total"]'].forEach(sel => {
      const newEl = tmp.querySelector(sel);
      const oldEl = document.querySelector(sel);
      if (newEl && oldEl && newEl !== newItems && oldEl !== oldItems) {
        oldEl.replaceWith(newEl);
      }
    });
  }

  // SAFETY NET 1: MutationObserver op cart-bubble. Sommige scripts overschrijven
  // window.fetch waardoor onze monkey-patch dood is. De bubble updaten gebeurt
  // alleen NA cart change — als we dat zien, refresh drawer + dispatch events.
  (function () {
    let scheduled = false;
    function scheduleRefresh() {
      if (scheduled) return;
      scheduled = true;
      setTimeout(async () => {
        scheduled = false;
        const sid = getCartHeaderSectionId();
        if (!sid) return;
        try {
          const r = await fetch('/?sections=' + encodeURIComponent(sid), { cache: 'no-store' });
          if (!r.ok) return;
          const data = await r.json();
          applyCartSections(data);
        } catch (e) { /* silent */ }
      }, 40);
    }
    function attachObserver() {
      const bubble = document.querySelector('[class*="cart-bubble"], cart-icon-bubble');
      if (!bubble || bubble.dataset.fluxObserved === '1') return;
      bubble.dataset.fluxObserved = '1';
      const obs = new MutationObserver(scheduleRefresh);
      obs.observe(bubble, { childList: true, subtree: true, characterData: true, attributes: true });
    }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', attachObserver);
    else attachObserver();
    // Re-attach na onze applyCartSections (vervangt de bubble dus oude observer is dood)
    document.addEventListener('cart:refresh', () => setTimeout(attachObserver, 50));
  })();
  // Global open cart drawer
  // V65: KLIK CART ICON EERST i.p.v. drawer.open() — Horizon's native flow
  // doet zelf een fresh /cart fetch + render (geen stale items).
  // drawer.open() programmatisch leverde lege drawer op.
  function openCartDrawerGlobal() {
    // V65 strategy: trigger Horizon's natuurlijke drawer flow via cart-icon click
    const cartBtn = document.querySelector(
      'header [aria-controls*="cart"], ' +
      'header a[href="/cart"], ' +
      'header button[aria-label*="winkelwagen" i], ' +
      'header button[aria-label*="cart" i], ' +
      'header [class*="cart-icon"], ' +
      'header [class*="cart-link"]'
    );
    if (cartBtn) {
      try { cartBtn.click(); } catch (e) {}
      // Fallback: also try drawer.open() if click didn't trigger
      setTimeout(() => {
        const drawer = document.querySelector('cart-drawer-component, cart-drawer, #cart-drawer');
        const isOpen = drawer && (drawer.hasAttribute('open') || drawer.classList.contains('is-open'));
        if (!isOpen && drawer && typeof drawer.open === 'function') {
          try { drawer.open(); } catch (e) {}
        }
      }, 200);
      return;
    }
    // No cart icon found — fall back to drawer methods
    document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
    const drawer = document.querySelector('cart-drawer-component, cart-drawer, #cart-drawer, [id*="cart-drawer"]');
    if (drawer) {
      if (typeof drawer.open === 'function') { drawer.open(); return; }
      if (typeof drawer.show === 'function') { drawer.show(); return; }
      if (drawer.tagName === 'DETAILS') { drawer.open = true; return; }
    }
  }
  // Expose helpers globally zodat alle scripts (incl. inline injects) ze kunnen gebruiken
  window.fluxCart = {
    getHeaderSectionId: getCartHeaderSectionId,
    applyCartSections: applyCartSections,
    openCartDrawer: openCartDrawerGlobal,
  };

  /* ================================================================
     UNIVERSAL ATC INTERCEPTOR
     Vangt elke "add to cart" op productpagina's (Horizon shopify-product-form
     én custom .bkp-form), gebruikt /cart/add.js met sections parameter,
     refresht cart drawer HTML, en opent de drawer met de nieuwe items
     DIRECT zichtbaar. Voorkomt stale drawer state.

     Skip: tool pages (eigen flow), payment buttons (Klarna/PayPal/etc),
     dynamic checkout buttons.
  ================================================================ */
  (function () {
    function isPaymentButton(btn) {
      if (!btn) return false;
      var attrs = (btn.className || '') + ' ' + (btn.id || '') + ' ' + (btn.getAttribute('name') || '');
      attrs = attrs.toLowerCase();
      return /shopify-payment|payment-terms|installments|paypal|google-pay|apple-pay|express-checkout|shop-pay|dynamic-checkout/.test(attrs);
    }
    function isToolPage() {
      // Tool pages hebben eigen ATC flow via .flux-st__add-cart-btn — die niet intercepten
      return !!document.querySelector('.flux-st[data-state]');
    }
    function findProductForm(el) {
      return el && el.closest ? el.closest('form.shopify-product-form, form[action*="/cart/add"]') : null;
    }
    function getVariantId(form, formData) {
      var v = formData.get('id');
      if (v) return parseInt(v, 10);
      var input = form.querySelector('input[name="id"], .bkp-variant-id');
      return input ? parseInt(input.value, 10) : 0;
    }
    function collectProperties(formData) {
      var props = {};
      for (var pair of formData.entries()) {
        if (pair[0].indexOf('properties[') === 0 && pair[0].slice(-1) === ']') {
          var k = pair[0].slice(11, -1);
          if (pair[1] != null && pair[1] !== '') props[k] = pair[1];
        }
      }
      return props;
    }
    async function universalAjaxAdd(form, btn) {
      var formData = new FormData(form);
      var vid = getVariantId(form, formData);
      if (!vid) { return false; }
      var qty = parseInt(formData.get('quantity') || '1', 10) || 1;
      var props = collectProperties(formData);
      var items = [{ id: vid, quantity: qty }];
      if (Object.keys(props).length) items[0].properties = props;
      // Inpakken als cadeau toevoegen als checkbox in dit form checked is
      var inpakChk = form.querySelector('.flux-st__inpak-inject-check:checked, .flux-st__inpak-check:checked:not(.flux-st__achter-check)');
      if (inpakChk) {
        var inpakVid = parseInt(inpakChk.dataset.variantId, 10);
        if (inpakVid && inpakVid !== vid) items.push({ id: inpakVid, quantity: 1 });
      }
      // V66: Tegel addon radio op productpagina (alle-tegels collectie)
      // Net als op de toolpagina's wordt de gekozen accessoire mee in dezelfde
      // /cart/add.js call toegevoegd, zodat tegel + addon samen in 1 actie
      // in de cart komen en de drawer ze direct laat zien.
      var tegelAddonRadio = form.querySelector('input[name="bkc-tegel-addon"]:checked');
      if (tegelAddonRadio && tegelAddonRadio.value) {
        var addonVid = parseInt(tegelAddonRadio.value, 10);
        if (addonVid && !items.some(function (it) { return it.id === addonVid; })) {
          items.push({ id: addonVid, quantity: 1 });
        }
      }
      var origText = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Toevoegen…'; }
      try {
        var r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items: items,
            sections: getCartHeaderSectionId(),
            sections_url: window.location.pathname
          })
        });
        if (!r.ok) {
          var errText = await r.text();
          throw new Error(errText.substring(0, 200));
        }
        var resp = await r.json();
        // KEY FIX: swap rendered drawer + bubble HTML zodat items DIRECT zichtbaar zijn
        if (resp && resp.sections) applyCartSections(resp.sections);
        // Trigger events (theme may listen)
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true, detail: { items: items } }));
        // 800ms wait — geeft Horizon's cart-items-component connectedCallback
        // ruim de tijd om z'n initial render te doen na de DOM swap. Bij 250ms
        // was de drawer soms nog leeg bij open (Horizon's interne setup nog niet klaar).
        await new Promise(r => setTimeout(r, 800));
        openCartDrawerGlobal();
        // DOUBLE-SAFETY: fetch fresh drawer SECTION 600ms later EN refresh nogmaals
        // — vangt edge cases waar de eerste swap niet doorkwam of Horizon's drawer
        // intern een snapshot heeft gecached. Tweede refresh = guaranteed items zichtbaar.
        setTimeout(async () => {
          try {
            const sid = getCartHeaderSectionId();
            if (!sid) return;
            const r2 = await fetch('/?sections=' + encodeURIComponent(sid), { cache: 'no-store' });
            if (r2.ok) {
              const data = await r2.json();
              applyCartSections(data);
            }
          } catch (e) { /* silent — was bonus refresh */ }
        }, 600);
        return true;
      } catch (err) {
        console.error('[fluxCart] ATC failed', err);
        alert('Toevoegen aan winkelwagen mislukt. Probeer het opnieuw.');
        return false;
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = origText; }
      }
    }
    // V66: Quick-add op productKAART (collection/homepage cards) — geen form,
    // alleen een knop met data-quick-add="<variantId>". Gebruikt dezelfde
    // sections-refresh flow als product page ATC.
    async function quickAddByVariantId(btn, variantId) {
      var origLabel = btn.querySelector('.bkc-btn-label') || btn.querySelector('span');
      var origText = origLabel ? origLabel.textContent : btn.textContent;
      btn.classList.add('is-loading');
      btn.disabled = true;
      try {
        var sectionId = getCartHeaderSectionId();
        var r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: sectionId,
            sections_url: window.location.pathname
          })
        });
        if (!r.ok) throw new Error('Add failed: ' + r.status);
        var resp = await r.json();
        if (resp && resp.sections) applyCartSections(resp.sections);
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:added', { bubbles: true }));
        btn.classList.remove('is-loading');
        btn.classList.add('is-added');
        if (origLabel) origLabel.textContent = 'Toegevoegd ✓'; else btn.textContent = 'Toegevoegd ✓';
        // Wacht 800ms (zelfde delay als productpagina ATC) voor drawer open
        await new Promise(function(r){ setTimeout(r, 800); });
        openCartDrawerGlobal();
        setTimeout(function () {
          btn.classList.remove('is-added');
          btn.disabled = false;
          if (origLabel) origLabel.textContent = origText; else btn.textContent = origText;
        }, 2000);
      } catch (err) {
        btn.classList.remove('is-loading');
        btn.disabled = false;
        if (origLabel) origLabel.textContent = 'Fout — opnieuw'; else btn.textContent = 'Fout — opnieuw';
        setTimeout(function () {
          if (origLabel) origLabel.textContent = origText; else btn.textContent = origText;
        }, 2000);
      }
    }

    function onAtcClick(e) {
      // Skip tool pages — hebben eigen flow
      if (isToolPage()) return;
      // V66: Eerst quick-add buttons (productkaarten — geen form, data-quick-add op de knop)
      var quickBtn = e.target.closest('[data-quick-add]');
      if (quickBtn && !quickBtn.closest('cart-drawer-component, [class*="cart-drawer"]')) {
        var vid = parseInt(quickBtn.dataset.quickAdd, 10);
        if (vid) {
          e.preventDefault();
          e.stopPropagation();
          quickAddByVariantId(quickBtn, vid);
          return;
        }
      }
      // Daarna: standaard product form ATC
      var btn = e.target.closest('button[name="add"], button.add-to-cart-button, button.bkp-cart-btn, button[type="submit"]');
      if (!btn) return;
      if (isPaymentButton(btn)) return;
      var form = findProductForm(btn);
      if (!form) return;
      // Skip als deze knop een ander doel heeft (bv. design-check toggle in cart drawer)
      if (btn.closest('cart-drawer-component, [class*="cart-drawer"], cart-items-component')) return;
      e.preventDefault();
      e.stopPropagation();
      universalAjaxAdd(form, btn);
    }
    function onFormSubmit(e) {
      if (isToolPage()) return;
      var form = e.target;
      if (!form.matches || !form.matches('form.shopify-product-form, form[action*="/cart/add"]')) return;
      if (form.closest('cart-drawer-component, [class*="cart-drawer"]')) return;
      e.preventDefault();
      e.stopPropagation();
      var btn = form.querySelector('button[name="add"], button[type="submit"]:not([class*="payment"]):not([class*="shop-pay"])');
      universalAjaxAdd(form, btn);
    }
    // Capture phase zodat we vóór theme handlers runnen
    document.addEventListener('click', onAtcClick, true);
    document.addEventListener('submit', onFormSubmit, true);
  })();

  async function syncInitialState(toggle) {
    try {
      const cart = await fetch('/cart.js', { cache: 'no-store' }).then(r => r.json());
      const has = (cart.items || []).some(i => i.variant_id === DESIGN_CHECK_VARIANT_ID);
      toggle.checked = has;
    } catch (e) { /* niet erg */ }
  }

  function bindDesignCheckToggles() {
    document.querySelectorAll('input.design-check__toggle:not([data-flux-bound])').forEach(toggle => {
      toggle.dataset.fluxBound = '1';
      toggle.addEventListener('change', () => syncDesignCheck(toggle));
      // GEEN syncInitialState meer — vertrouw server-rendered checked state.
      // Liquid rendert toggle.checked op basis van cart inhoud bij elke section re-render.
    });
  }
  bindDesignCheckToggles();
  /* Re-bind wanneer cart drawer opnieuw rendert */
  const observer = new MutationObserver(() => bindDesignCheckToggles());
  observer.observe(document.body, { childList: true, subtree: true });

  const FRAME_RATIO = 0.84; // 84% van de container = het witte frame
  const FRAME_OFFSET = (1 - FRAME_RATIO) / 2; // 8% aan elke kant

  function init(root) {
    const cfg = {
      backendUrl: root.dataset.backendUrl,
      widgetToken: root.dataset.widgetToken,
      shopDomain: root.dataset.shopDomain,
      maxMp: parseFloat(root.dataset.maxMp || '4'),
      outputSize: parseInt(root.dataset.outputSize || '2048', 10),
      prompt: root.dataset.prompt || '',
      provider: root.dataset.provider || 'flux',
      mode: root.dataset.mode || 'ai', // 'ai' (default) of 'upload-only'
    };
    let currentVariantId = parseInt(root.dataset.variantId, 10);
    let currentAddonId = null;

    const els = {
      file: root.querySelector('.flux-st__file'),
      uploadCta: root.querySelector('.flux-st__upload-cta'),
      generating: root.querySelector('.flux-st__generating'),
      croppedPreview: root.querySelector('.flux-st__cropped-preview'),
      progressBar: root.querySelector('.flux-st__progress-bar'),
      progressText: root.querySelector('.flux-st__progress-text'),
      result: root.querySelector('.flux-st__result'),
      hoekOverlay: root.querySelector('.flux-st__hoek-overlay'),
      hoekRadios: root.querySelectorAll('.flux-st__hoek-radio'),
      postActions: root.querySelector('.flux-st__post-actions'),
      changeBtn: root.querySelector('.flux-st__change-btn'),
      recropBtn: root.querySelector('.flux-st__recrop-btn'),
      addCartBtn: root.querySelector('.flux-st__add-to-cart'),
      // Text-tool specifiek
      textInput: root.querySelector('.flux-st__text-input'),
      textOverlay: root.querySelector('.flux-st__text-overlay'),
      textContent: root.querySelector('.flux-st__text-content'),
      textBg: root.querySelector('.flux-st__text-bg'),
      textCounter: root.querySelector('[data-counter]'),
      textSizeInputs: root.querySelectorAll('input[type=radio][name^="flux-st-text-size-"]'),
      error: root.querySelector('.flux-st__error'),
      variantInputs: root.querySelectorAll('input[type=radio][name^="flux-st-variant-"]'),
      addonInputs: root.querySelectorAll('input[type=radio][name^="flux-st-addon-"]'),
      modal: root.querySelector('.flux-st-modal'),
      modalCrop: root.querySelector('.flux-st-modal__crop'),
      modalImg: root.querySelector('.flux-st-modal__img'),
      modalBackdrop: root.querySelector('.flux-st-modal__backdrop'),
      modalCancel: root.querySelector('.flux-st-modal__cancel'),
      modalConfirm: root.querySelector('.flux-st-modal__confirm'),
      modalZoomBtns: root.querySelectorAll('.flux-st-modal__icon-btn[data-zoom]'),
    };

    // Bulletproof state setter — togglet `hidden` rechtstreeks op elk element.
    function setState(state) {
      root.dataset.state = state;
      const isEmpty = state === 'empty';
      const isGen = state === 'generating';
      const isResult = state === 'result';
      if (els.uploadCta) els.uploadCta.hidden = !isEmpty;
      if (els.generating) els.generating.hidden = !isGen;
      if (els.result) els.result.hidden = !isResult;
      // Draggable text overlay: voor upload-only/ai alleen in result state. Voor mok altijd zichtbaar.
      const draggableOverlay = root.querySelector('.flux-st__text-overlay--draggable');
      if (draggableOverlay && cfg.mode !== 'mok') {
        draggableOverlay.hidden = !isResult;
      }
    }

    let originalFile = null;
    let originalBlob = null;
    let croppedDataUrl = null;
    let resultUrl = null;
    let cropper = null;
    let progressTimer = null;

    // SAFETY: clear any leftover server-rendered/cached error text bij init
    if (els.error) { els.error.textContent = ''; els.error.hidden = true; }

    // Configuratie check — NOOIT customer-facing tonen (setup melding voor admin).
    // Log naar console en blokkeer init NIET — laat de tool werken; specifieke
    // backend-afhankelijke acties (Vercel upload, AI call) falen pas op het moment
    // dat ze nodig zijn met een gerichte error-handler.
    if (!cfg.backendUrl || !cfg.widgetToken) {
      console.warn('[flux] backend URL of widget token niet ingesteld in theme editor — sommige features (AI / cart-preview uploads) werken niet tot dit ingevuld is.');
    }
    if (cfg.mode === 'ai' && !cfg.prompt) {
      console.warn('[flux] geen AI-prompt voor dit product — vul custom.ai_prompt metafield in.');
      // Voor AI mode is prompt vereist; tool kan niet zinvol opstarten zonder.
      // Verberg de tool UI compleet zodat klant geen kapotte versie ziet.
      const sidebar = root.querySelector('.flux-st__sidebar');
      const preview = root.querySelector('.flux-st__preview');
      if (sidebar) sidebar.style.display = 'none';
      if (preview) preview.style.display = 'none';
      return;
    }
    /* In maps mode hebben we de Mapbox token nodig — gecheckt verderop bij init */

    // Pills
    els.variantInputs.forEach((input) => {
      input.addEventListener('change', (e) => {
        currentVariantId = parseInt(e.target.value, 10);
        syncPillChecked(els.variantInputs, e.target);
      });
    });
    els.addonInputs.forEach((input) => {
      input.addEventListener('change', (e) => {
        currentAddonId = e.target.value ? parseInt(e.target.value, 10) : null;
        syncPillChecked(els.addonInputs, e.target);
      });
    });

    // File upload
    els.file.addEventListener('change', (e) => {
      hideError();
      const file = e.target.files[0];
      if (!file) return;
      openCropperModal(file).catch((err) => showError('Kon de foto niet laden: ' + err.message));
    });

    els.changeBtn?.addEventListener('click', () => { els.file.value = ''; els.file.click(); });
    els.recropBtn?.addEventListener('click', () => { if (originalBlob) openModalWithBlob(originalBlob); });

    // Modal controls
    els.modalCancel.addEventListener('click', closeModal);
    els.modalBackdrop.addEventListener('click', closeModal);
    els.modalConfirm.addEventListener('click', confirmAndGenerate);
    els.modalZoomBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (cropper) cropper.zoom(btn.dataset.zoom === 'in' ? 1.2 : 1 / 1.2);
      });
    });

    // Hoekornament radio — bij selectie van "Sierlijk" overlay tonen
    let currentHoekUrl = '';
    if (els.hoekRadios && els.hoekRadios.length && els.hoekOverlay) {
      els.hoekRadios.forEach(input => {
        input.addEventListener('change', () => {
          syncPillChecked(els.hoekRadios, input);
          currentHoekUrl = input.dataset.hoekUrl || '';
          if (currentHoekUrl && root.dataset.state === 'result') {
            els.hoekOverlay.src = currentHoekUrl;
            els.hoekOverlay.hidden = false;
          } else {
            els.hoekOverlay.hidden = true;
          }
        });
      });
    }

    // -------- TEXT controls wiring (text-tool EN upload-only) --------
    let currentTextSize = 'middel';
    let currentTextContent = '';
    let currentTextColor = '#000000';
    let uploadTextPos = { x: 50, y: 50 }; // percentage van tile (alleen upload-only)
    const isTextMode = cfg.mode === 'text-tool';
    const isUploadMode = cfg.mode === 'upload-only';
    const isAiMode = cfg.mode === 'ai';
    const isValMode = cfg.mode === 'valentijn';
    const isMokMode = cfg.mode === 'mok';
    const isTshirtMode = cfg.mode === 'tshirt';
    const isMokLike = isMokMode || isTshirtMode;
    const isMapsMode = cfg.mode === 'maps';
    // Modes die de draggable overlay gebruiken (text input + size + color + drag)
    const hasDraggableText = isUploadMode || isAiMode || isMokLike;

    if ((isTextMode || hasDraggableText) && els.textInput && els.textOverlay && els.textContent) {
      const placeholderText = els.textContent.textContent || '';
      if (isTextMode) els.textContent.classList.add('flux-st__text-content--placeholder');

      function updateTextOverlay() {
        const val = els.textInput.value;
        currentTextContent = val.trim();
        // Pointer-events togglen op draggable overlay zodat lege overlay niet blokkeert
        if (hasDraggableText && els.textOverlay) {
          els.textOverlay.style.pointerEvents = currentTextContent ? 'auto' : 'none';
        }
        if (currentTextContent) {
          els.textContent.textContent = val;
          els.textContent.classList.remove('flux-st__text-content--placeholder');
          if (isTextMode) els.addCartBtn.disabled = false;
        } else {
          if (isTextMode) {
            els.textContent.textContent = placeholderText;
            els.textContent.classList.add('flux-st__text-content--placeholder');
            els.addCartBtn.disabled = true;
          } else {
            // upload-only: lege tekst = geen overlay tekst (cart enabled via foto crop)
            els.textContent.textContent = '';
          }
        }
        if (els.textCounter) {
          const max = els.textInput.maxLength || 120;
          els.textCounter.textContent = val.length + ' / ' + max;
        }
      }
      els.textInput.addEventListener('input', updateTextOverlay);
      updateTextOverlay(); // init counter

      els.textSizeInputs.forEach(input => {
        input.addEventListener('change', (e) => {
          currentTextSize = e.target.value;
          els.textOverlay.dataset.size = currentTextSize;
          syncPillChecked(els.textSizeInputs, e.target);
        });
      });
    }

    // Force text overlay default center positie + initieel non-blocking
    // (pointer-events none tot er tekst is — anders blokkeert lege overlay upload-cta in mok)
    // V65: setProperty met 'important' priority — overschrijft ELKE CSS rule
    // (inclusief 'inset: auto !important' uit oude flux-style-transfer.css).
    if (hasDraggableText && els.textOverlay) {
      els.textOverlay.style.setProperty('position', 'absolute', 'important');
      els.textOverlay.style.setProperty('left', '50%', 'important');
      els.textOverlay.style.setProperty('top', '50%', 'important');
      els.textOverlay.style.setProperty('right', 'auto', 'important');
      els.textOverlay.style.setProperty('bottom', 'auto', 'important');
      els.textOverlay.style.setProperty('transform', 'translate(-50%, -50%)', 'important');
      els.textOverlay.style.setProperty('display', 'inline-block', 'important');
      els.textOverlay.style.setProperty('max-width', '85%', 'important');
      els.textOverlay.dataset.x = '50';
      els.textOverlay.dataset.y = '50';
      els.textOverlay.style.pointerEvents = 'none';
      uploadTextPos = { x: 50, y: 50 };
    }
    // -------- Color swatches + drag overlay (upload-only EN ai) --------
    if (hasDraggableText && els.textOverlay) {
      const colorRadios = root.querySelectorAll('input[type=radio][name^="flux-st-text-color-"]');
      const colorCustom = root.querySelector('.flux-st__color-custom');
      const swatchEls = root.querySelectorAll('.flux-st__swatch');

      function applyColor() {
        if (els.textOverlay) els.textOverlay.style.color = currentTextColor;
        // Direct ook op content (overrides base CSS color regel)
        if (els.textContent) els.textContent.style.color = currentTextColor;
      }
      function checkSwatch(el) {
        swatchEls.forEach(s => s.classList.toggle('flux-st__swatch--checked', s === el));
      }
      colorRadios.forEach(input => {
        input.addEventListener('change', (e) => {
          currentTextColor = e.target.value;
          applyColor();
          checkSwatch(input.closest('.flux-st__swatch'));
        });
      });
      if (colorCustom) {
        colorCustom.addEventListener('input', (e) => {
          currentTextColor = e.target.value;
          applyColor();
          checkSwatch(colorCustom.closest('.flux-st__swatch'));
        });
      }

      // Drag handlers — use offsetParent zodat we correct werken in mok mode
      // (waar de overlay binnen .flux-st__mok-photo-wrap zit, niet direct binnen tile)
      let dragging = false;
      let startX = 0, startY = 0, startPosX = 50, startPosY = 50;
      els.textOverlay.addEventListener('pointerdown', (e) => {
        if (els.textOverlay.hidden) return;
        dragging = true;
        els.textOverlay.classList.add('is-dragging');
        try { els.textOverlay.setPointerCapture(e.pointerId); } catch {}
        startX = e.clientX; startY = e.clientY;
        startPosX = uploadTextPos.x; startPosY = uploadTextPos.y;
        e.preventDefault();
      });
      els.textOverlay.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const parent = els.textOverlay.offsetParent || root.querySelector('.flux-st__tile');
        if (!parent) return;
        const rect = parent.getBoundingClientRect();
        if (!rect.width) return;
        const dxP = ((e.clientX - startX) / rect.width) * 100;
        const dyP = ((e.clientY - startY) / rect.height) * 100;
        uploadTextPos.x = Math.max(5, Math.min(95, startPosX + dxP));
        uploadTextPos.y = Math.max(5, Math.min(95, startPosY + dyP));
        // V65: setProperty + !important — anders wint oude CSS 'inset:auto !important'
        // van onze inline left/top → drag had geen effect, tekst bleef in hoek.
        els.textOverlay.style.setProperty('left', uploadTextPos.x + '%', 'important');
        els.textOverlay.style.setProperty('top', uploadTextPos.y + '%', 'important');
        els.textOverlay.dataset.x = uploadTextPos.x.toFixed(1);
        els.textOverlay.dataset.y = uploadTextPos.y.toFixed(1);
      });
      const stopDrag = () => { dragging = false; els.textOverlay.classList.remove('is-dragging'); };
      els.textOverlay.addEventListener('pointerup', stopDrag);
      els.textOverlay.addEventListener('pointercancel', stopDrag);
    }

    // -------- Render upload-only tile (cropped photo + optionele tekst) --------
    async function renderAndUploadUploadOnlyTile() {
      const size = cfg.outputSize || 2048;
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      // 1. Cropped photo tekenen (vult tegel)
      const photoImg = await loadImageDataUrl(croppedDataUrl);
      ctx.drawImage(photoImg, 0, 0, size, size);
      // 2. Optioneel tekst overlay
      const text = (currentTextContent || '').trim();
      if (text) {
        ctx.fillStyle = currentTextColor || '#ffffff';
        ctx.font = `700 ${textPxSize(size, currentTextSize)}px "Cormorant Garamond", Georgia, serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(0,0,0,0.45)';
        ctx.shadowBlur = size * 0.005;
        ctx.shadowOffsetY = size * 0.0025;
        const maxWidth = size * 0.7;
        const lines = wrapText(ctx, text, maxWidth);
        const lineHeight = textPxSize(size, currentTextSize) * 1.15;
        const totalHeight = lines.length * lineHeight;
        const cx = size * (uploadTextPos.x / 100);
        const cy = size * (uploadTextPos.y / 100);
        const startY = cy - totalHeight / 2 + lineHeight / 2;
        lines.forEach((line, i) => {
          ctx.fillText(line, cx, startY + i * lineHeight);
        });
        ctx.shadowColor = 'transparent';
      }
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      const uploadUrl = root.dataset.uploadEndpoint || cfg.backendUrl.replace(/\/style-transfer$/, '/upload-image');
      const r = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_base64: dataUrl,
          widget_token: cfg.widgetToken,
          filename: 'upload-tile-' + Date.now() + '.jpg'
        })
      });
      if (!r.ok) throw new Error('Upload status ' + r.status);
      const data = await r.json();
      if (!data.image_url) throw new Error('Geen image URL ontvangen.');
      return data.image_url;
    }
    function loadImageDataUrl(dataUrl) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image load failed'));
        img.src = dataUrl;
      });
    }

    // -------- Render text-tile naar canvas + upload --------
    async function renderAndUploadTextTile() {
      const size = cfg.outputSize || 2048;
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      // 1. Achtergrond image tekenen
      const bgUrl = root.dataset.textBg || els.textBg?.src;
      if (bgUrl) {
        try {
          const bgImg = await loadCorsImage(bgUrl);
          ctx.drawImage(bgImg, 0, 0, size, size);
        } catch (e) {
          // fallback witte achtergrond
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, size, size);
        }
      } else {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, size, size);
      }
      // 2. Tekst tekenen — centered, met word wrap, matching overlay style
      const text = (currentTextContent || '').trim();
      if (!text) throw new Error('Geen tekst om op te slaan.');
      ctx.fillStyle = '#1e3a78';
      ctx.font = `italic 600 ${textPxSize(size, currentTextSize)}px "Cormorant Garamond", "EB Garamond", Georgia, serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const lines = wrapText(ctx, text, size * 0.56); // max width = 56% van tegel (binnen witte cirkel)
      const lineHeight = textPxSize(size, currentTextSize) * 1.15;
      const totalHeight = lines.length * lineHeight;
      const startY = size / 2 - totalHeight / 2 + lineHeight / 2;
      lines.forEach((line, i) => {
        ctx.fillText(line, size / 2, startY + i * lineHeight);
      });
      // 3. Canvas → dataURL → upload
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      const uploadUrl = root.dataset.uploadEndpoint || cfg.backendUrl.replace(/\/style-transfer$/, '/upload-image');
      const r = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_base64: dataUrl,
          widget_token: cfg.widgetToken,
          filename: 'text-tile-' + Date.now() + '.jpg'
        })
      });
      if (!r.ok) throw new Error('Upload status ' + r.status);
      const data = await r.json();
      if (!data.image_url) throw new Error('Geen image URL ontvangen.');
      return data.image_url;
    }
    function textPxSize(canvasSize, sizeKey) {
      // Verhouding op basis van canvas size — komt overeen met clamp() in CSS
      const ratios = { klein: 0.038, middel: 0.062, groot: 0.092 };
      return Math.round(canvasSize * (ratios[sizeKey] || ratios.middel));
    }
    function wrapText(ctx, text, maxWidth) {
      // Splits op spaties EN op newlines van textarea
      const rawLines = text.split(/\n/);
      const out = [];
      rawLines.forEach(raw => {
        const words = raw.split(/\s+/).filter(Boolean);
        if (!words.length) { out.push(''); return; }
        let line = words[0];
        for (let i = 1; i < words.length; i++) {
          const test = line + ' ' + words[i];
          if (ctx.measureText(test).width > maxWidth) {
            out.push(line);
            line = words[i];
          } else {
            line = test;
          }
        }
        out.push(line);
      });
      return out;
    }

    // Cart — origText capture ÉÉN keer aan het begin, daarna geen shadowing
    const originalCartLabel = els.addCartBtn.textContent;
    els.addCartBtn.addEventListener('click', async () => {
      // In text-tool mode: render + upload eerst, dan cart
      if (cfg.mode === 'text-tool') {
        if (!currentTextContent) { showError('Vul eerst een tekst in.'); return; }
        els.addCartBtn.disabled = true;
        els.addCartBtn.textContent = 'Tekst opslaan…';
        try {
          resultUrl = await renderAndUploadTextTile();
        } catch (err) {
          showError('Tekst opslaan mislukt: ' + err.message);
          els.addCartBtn.disabled = false;
          els.addCartBtn.textContent = originalCartLabel;
          return;
        }
      }
      // In upload-only mode: render photo + optionele tekst → upload pas bij cart
      if (cfg.mode === 'upload-only') {
        if (!croppedDataUrl) { showError('Upload en snij eerst een foto bij.'); return; }
        els.addCartBtn.disabled = true;
        els.addCartBtn.textContent = 'Foto opslaan…';
        try {
          resultUrl = await renderAndUploadUploadOnlyTile();
        } catch (err) {
          showError('Foto opslaan mislukt: ' + err.message);
          els.addCartBtn.disabled = false;
          els.addCartBtn.textContent = originalCartLabel;
          return;
        }
      }
      // In mok/tshirt mode: render PREVIEW (met mockup) + PRINT FILE (witte bg met foto+tekst)
      let mokPrintUrl = null;
      let tshirtBackPreviewUrl = null;
      let tshirtBackPrintUrl = null;
      let tshirtBackState = null;
      if (isMokLike) {
        // In tshirt mode: save current side state naar tshirtSides voor render
        if (isTshirtMode) {
          // Sync current working vars → tshirtSides[currentTshirtSide]
          const s = tshirtSides[currentTshirtSide];
          s.photoUrl = croppedDataUrl;
          s.originalFile = originalFile;
          s.photoPos = { ...mokPhotoPos };
          s.photoSize = mokPhotoSize;
          s.customWidthPct = customPhotoWidthPct;
          s.textContent = currentTextContent;
          s.textPos = { ...uploadTextPos };
          s.textSize = currentTextSize;
          s.textColor = currentTextColor;
        }
        // Voorkant moet altijd een foto hebben
        const frontPhotoUrl = isTshirtMode ? tshirtSides.front.photoUrl : croppedDataUrl;
        if (!frontPhotoUrl) {
          showError(isTshirtMode ? 'Upload eerst een foto op de voorkant.' : 'Upload eerst een foto.');
          return;
        }
        els.addCartBtn.disabled = true;
        els.addCartBtn.textContent = 'Foto opslaan…';
        try {
          // Front state (in tshirt mode = explicit front state, anders = working state)
          const frontState = isTshirtMode ? tshirtSides.front : null;
          const frontTag = isTshirtMode ? 'voorkant' : null;
          // Twee uploads parallel — preview met mockup + print file zonder mockup
          [resultUrl, mokPrintUrl] = await Promise.all([
            renderAndUploadMokTile(frontState, frontTag),
            renderAndUploadMokPrintFile(frontState, frontTag),
          ]);
          // T-shirt achterkant — alleen als checkbox checked EN back heeft photo of text
          if (isTshirtMode) {
            const achterCheck = root.querySelector('.flux-st__achter-check');
            const back = tshirtSides.back;
            const hasBackDesign = back.photoUrl || (back.textContent && back.textContent.trim());
            if (achterCheck && achterCheck.checked && hasBackDesign) {
              els.addCartBtn.textContent = 'Achterkant opslaan…';
              [tshirtBackPreviewUrl, tshirtBackPrintUrl] = await Promise.all([
                renderAndUploadMokTile(back, 'achterkant'),
                renderAndUploadMokPrintFile(back, 'achterkant'),
              ]);
              tshirtBackState = back;
            }
          }
        } catch (err) {
          showError('Foto opslaan mislukt: ' + err.message);
          els.addCartBtn.disabled = false;
          els.addCartBtn.textContent = originalCartLabel;
          return;
        }
      }
      // In maps mode: build Static Image met pin → upload naar Blob
      if (cfg.mode === 'maps') {
        if (!mapsState.pinLng || !mapsState.pinLat) { showError('Plaats eerst een pin op de kaart.'); return; }
        els.addCartBtn.disabled = true;
        els.addCartBtn.textContent = 'Kaart opslaan…';
        try {
          resultUrl = await renderAndUploadMapsTile();
        } catch (err) {
          showError('Kaart opslaan mislukt: ' + err.message);
          els.addCartBtn.disabled = false;
          els.addCartBtn.textContent = originalCartLabel;
          return;
        }
      }
      // In valentijn mode: render polaroid composite + upload
      if (cfg.mode === 'valentijn') {
        if (!croppedDataUrl) { showError('Upload eerst een foto.'); return; }
        els.addCartBtn.disabled = true;
        els.addCartBtn.textContent = 'Foto opslaan…';
        try {
          resultUrl = await renderAndUploadValentijnTile();
        } catch (err) {
          showError('Foto opslaan mislukt: ' + err.message);
          els.addCartBtn.disabled = false;
          els.addCartBtn.textContent = originalCartLabel;
          return;
        }
      }
      // In AI mode met tekst: composite AI image + text → upload, overwrite resultUrl
      if (cfg.mode === 'ai' && currentTextContent) {
        els.addCartBtn.disabled = true;
        els.addCartBtn.textContent = 'Tekst toevoegen…';
        try {
          resultUrl = await renderAndUploadAiWithText();
        } catch (err) {
          showError('Tekst opslaan mislukt: ' + err.message);
          els.addCartBtn.disabled = false;
          els.addCartBtn.textContent = originalCartLabel;
          return;
        }
      }
      if (!resultUrl) return;
      els.addCartBtn.disabled = true;
      els.addCartBtn.textContent = 'Bezig…';
      try {
        let finalImageUrl = resultUrl;
        const hoekActive = currentHoekUrl && els.hoekOverlay;
        if (hoekActive) {
          els.addCartBtn.textContent = 'Hoekornament toevoegen…';
          try {
            finalImageUrl = await composeAndUpload(resultUrl, currentHoekUrl);
          } catch (e) {
            console.warn('Composite upload failed, using raw AI URL', e);
            finalImageUrl = resultUrl;
          }
        }
        // V78: GUARANTEE permanent URL — voorkom dat BFL signed URLs (10 min
        // expiry → Azure AuthenticationFailed) in cart line items terechtkomen.
        // Vercel Blob public URLs hebben pattern .public.blob.vercel-storage.com.
        // Alle andere URLs (BFL, OpenAI temp, etc.) worden ge-rehosted.
        if (finalImageUrl && !/\.public\.blob\.vercel-storage\.com\//.test(finalImageUrl)) {
          els.addCartBtn.textContent = 'Opslaan…';
          try {
            const r = await fetch(finalImageUrl);
            if (!r.ok) throw new Error('Fetch ' + r.status);
            const b = await r.blob();
            const du = await new Promise((res, rej) => {
              const rd = new FileReader();
              rd.onloadend = () => res(rd.result);
              rd.onerror = rej;
              rd.readAsDataURL(b);
            });
            finalImageUrl = await uploadDataUrl(du, 'ai-permanent-' + Date.now() + '.jpg');
          } catch (e) {
            console.warn('[V78] Permanent rehost failed, keeping original URL', e);
          }
        }
        // V79: Klantfoto (origineel) — altijd de door de klant aangeleverde bronfoto
        // als permanente link in de order zetten. Voor t-shirt is dit de voorkant-foto;
        // de achterkant krijgt zijn eigen link verderop. Modes zonder klantfoto
        // (maps/text-tool/valentijn) hebben geen croppedDataUrl → property wordt overgeslagen.
        let customerPhotoUrl = null;
        {
          const srcPhoto = (cfg.mode === 'tshirt') ? (tshirtSides.front && tshirtSides.front.photoUrl) : croppedDataUrl;
          if (srcPhoto) {
            els.addCartBtn.textContent = 'Foto bewaren…';
            customerPhotoUrl = await ensureCustomerPhotoUrl(srcPhoto);
          }
        }
        // BELANGRIJK: propertyKey bepalen VOOR properties object zodat we geen dubbele entries krijgen
        let propertyKey = 'Jouw ontwerp';
        if (cfg.mode === 'upload-only') propertyKey = 'Jouw foto';
        if (cfg.mode === 'text-tool') propertyKey = 'Tegel preview';
        if (cfg.mode === 'valentijn') propertyKey = 'Tegel preview';
        if (cfg.mode === 'mok') propertyKey = 'Mok preview';
        if (cfg.mode === 'tshirt') propertyKey = 'T-shirt preview';
        if (cfg.mode === 'maps') propertyKey = 'Kaart preview';
        const properties = {
          [propertyKey]: finalImageUrl,
          '_originele_filename': originalFile?.name || ((cfg.mode === 'text-tool' || cfg.mode === 'valentijn') ? 'tegel.jpg' : 'foto.jpg'),
          // _preview = hidden voor klant in cart display, gebruikt door cart-drawer JS om de line-item-image te swappen
          '_preview': finalImageUrl,
        };
        // V79: link naar de originele klantfoto — zichtbaar in de order zodat we
        // de bronfoto altijd terug kunnen vinden (los van het bewerkte ontwerp).
        if (customerPhotoUrl) properties['Klantfoto (origineel)'] = customerPhotoUrl;
        if (cfg.mode === 'text-tool') {
          properties['Jouw tekst'] = currentTextContent;
          properties['Tekstgrootte'] = currentTextSize;
        }
        if (cfg.mode === 'upload-only' && currentTextContent) {
          properties['Tekst op foto'] = currentTextContent;
          properties['Tekstgrootte'] = currentTextSize;
          properties['Tekstkleur'] = currentTextColor;
        }
        if (cfg.mode === 'ai' && currentTextContent) {
          properties['Tekst op foto'] = currentTextContent;
          properties['Tekstgrootte'] = currentTextSize;
          properties['Tekstkleur'] = currentTextColor;
        }
        if (cfg.mode === 'valentijn') {
          properties['Namen'] = valentijnNames || '(geen)';
          properties['Datum'] = valentijnDate || '(geen)';
        }
        if (cfg.mode === 'maps') {
          if (mapsState.placeName) properties['Locatie'] = mapsState.placeName;
          properties['Coördinaten'] = mapsState.pinLat.toFixed(5) + ', ' + mapsState.pinLng.toFixed(5);
          properties['Google Maps'] = 'https://www.google.com/maps?q=' + mapsState.pinLat.toFixed(6) + ',' + mapsState.pinLng.toFixed(6);
          if (mapsState.caption) properties['Tekst op kaart'] = mapsState.caption;
          const styleLabels = { klassiek: 'Klassiek', natuur: 'Natuur', satelliet: 'Satelliet', minimaal: 'Minimaal' };
          properties['Kaartstijl'] = styleLabels[mapsStyleKey] || 'Klassiek';
        }
        if (isMokLike) {
          // Mok/T-shirt preview is al gezet via propertyKey hierboven — alleen extras toevoegen
          if (mokPrintUrl) properties['_Print bestand'] = mokPrintUrl;
          const variantLabelKey = cfg.mode === 'tshirt' ? 'T-shirt variant' : 'Mok kleur';
          properties[variantLabelKey] = currentMokColorLabel;
          // In tshirt mode wijzen properties naar VOORKANT state (front)
          const frontStateForProps = isTshirtMode ? tshirtSides.front : null;
          properties['Foto formaat'] = frontStateForProps ? frontStateForProps.photoSize : mokPhotoSize;
          const frontText = frontStateForProps ? frontStateForProps.textContent : currentTextContent;
          const frontTextSize = frontStateForProps ? frontStateForProps.textSize : currentTextSize;
          const frontTextColor = frontStateForProps ? frontStateForProps.textColor : currentTextColor;
          if (frontText) {
            properties['Tekst op foto'] = frontText;
            properties['Tekstgrootte'] = frontTextSize;
            properties['Tekstkleur'] = frontTextColor;
          }
        }
        if (hoekActive) properties['Hoekornament'] = 'Sierlijk';
        const items = [{ id: currentVariantId, quantity: 1, properties }];
        if (currentAddonId) items.push({ id: currentAddonId, quantity: 1 });
        // T-shirt: achterkant addon (handle: achterkant-ontwerp) als checkbox checked + back is gerenderd
        if (cfg.mode === 'tshirt' && tshirtBackPreviewUrl && tshirtBackState) {
          const achterCheck = root.querySelector('.flux-st__achter-check');
          const achterVid = achterCheck ? parseInt(achterCheck.dataset.variantId, 10) : NaN;
          if (achterVid) {
            const backProps = {
              'Achterkant preview': tshirtBackPreviewUrl,
              '_originele_filename': tshirtBackState.originalFile?.name || 'achterkant.jpg',
              '_preview': tshirtBackPreviewUrl,
              'Hoort bij': document.title.replace(/\s*[-–|]\s*Bedruk King\s*$/i, '').trim() || 'T-shirt',
            };
            if (tshirtBackPrintUrl) backProps['_Print bestand'] = tshirtBackPrintUrl;
            backProps['Foto formaat'] = tshirtBackState.photoSize;
            if (tshirtBackState.textContent) {
              backProps['Tekst op achterkant'] = tshirtBackState.textContent;
              backProps['Tekstgrootte'] = tshirtBackState.textSize;
              backProps['Tekstkleur'] = tshirtBackState.textColor;
            }
            // V79: originele klantfoto van de achterkant als permanente link
            const backCustomerUrl = await ensureCustomerPhotoUrl(
              tshirtBackState.photoUrl || (tshirtSides.back && tshirtSides.back.photoUrl)
            );
            if (backCustomerUrl) backProps['Klantfoto (origineel)'] = backCustomerUrl;
            items.push({ id: achterVid, quantity: 1, properties: backProps });
          }
        }
        // Inpakken als cadeau — universeel voor ALLE tool modes (was alleen mok).
        // De achterkant-ontwerp checkbox heeft ook class flux-st__inpak-check;
        // die filteren we eruit zodat we hem niet dubbel toevoegen.
        const inpakChks = root.querySelectorAll('.flux-st__inpak-check:not(.flux-st__achter-check)');
        inpakChks.forEach(chk => {
          if (chk.checked) {
            const inpakVid = parseInt(chk.dataset.variantId, 10);
            if (inpakVid && !items.some(it => it.id === inpakVid)) {
              items.push({ id: inpakVid, quantity: 1 });
            }
          }
        });
        // Include sections zodat cart drawer + cart icon direct ge-rerendered worden
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items,
            sections: getCartHeaderSectionId(),
            sections_url: window.location.pathname
          }),
        });
        if (!r.ok) throw new Error(await r.text());
        const addResp = await r.json();
        // Swap rendered section HTML (extract cart-drawer-component + cart-bubble uit header section)
        if (addResp && addResp.sections) applyCartSections(addResp.sections);
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:updated'));
        // Open cart drawer (geen redirect naar /cart)
        openCartDrawer();
        // Vervang line-item images door preview (mok preview etc.)
        setTimeout(swapCartPreviewImages, 350);
        setTimeout(swapCartPreviewImages, 1200);
        els.addCartBtn.textContent = 'Toegevoegd ✓';
        setTimeout(() => {
          els.addCartBtn.disabled = false;
          els.addCartBtn.textContent = originalCartLabel;
        }, 1500);
      } catch (err) {
        showError('Toevoegen aan winkelwagen mislukt: ' + err.message);
        els.addCartBtn.disabled = false;
        els.addCartBtn.textContent = originalCartLabel;
      }
    });

    // -------- Cart preview image swap — vervang line-item img door _preview URL --------
    // Cached cart data zodat repeated calls niet steeds /cart.js fetchen
    window.__fluxPreviewCache = window.__fluxPreviewCache || { ts: 0, map: {} };
    async function refreshPreviewCache() {
      try {
        const cart = await fetch('/cart.js', { credentials: 'include', cache: 'no-store' }).then(r => r.json());
        if (!cart || !cart.items) return;
        const map = {};
        cart.items.forEach(item => {
          const preview = item.properties && (item.properties._preview || item.properties['_preview']);
          if (preview) map[item.key] = preview;
        });
        window.__fluxPreviewCache = { ts: Date.now(), map };
      } catch (e) { /* silent */ }
    }
    function applyCartImageSwap() {
      const map = window.__fluxPreviewCache.map || {};
      if (!Object.keys(map).length) return;
      const lineSelectors = [
        '[data-cart-line-key]', '[data-line-key]', '[data-key]', '[data-cart-line]',
        'cart-item', '.cart-item', '.cart-line', '.cart-drawer__item', 'li[id*="CartItem"]'
      ];
      document.querySelectorAll(lineSelectors.join(',')).forEach(line => {
        const key = line.dataset.cartLineKey || line.dataset.lineKey || line.dataset.key || line.getAttribute('data-cart-line') || line.id?.replace(/^CartItem-/i, '');
        if (!key || !map[key]) return;
        const img = line.querySelector('img');
        if (img && img.src !== map[key]) {
          img.src = map[key];
          img.removeAttribute('srcset');
          img.dataset.fluxPreviewApplied = '1';
        }
      });
    }
    async function swapCartPreviewImages() {
      // Refresh cache if older than 2s
      if (Date.now() - window.__fluxPreviewCache.ts > 2000) {
        await refreshPreviewCache();
      }
      applyCartImageSwap();
    }
    // Hook events
    document.addEventListener('cart:refresh', () => setTimeout(swapCartPreviewImages, 500));
    document.addEventListener('cart:updated', swapCartPreviewImages);
    document.addEventListener('cart:open', () => setTimeout(swapCartPreviewImages, 300));
    // MutationObserver — apply swap snel (alleen DOM update, geen cart fetch)
    if (!window.__fluxCartObserver) {
      window.__fluxCartObserver = new MutationObserver(() => {
        clearTimeout(window.__fluxCartSwapDebounce);
        window.__fluxCartSwapDebounce = setTimeout(applyCartImageSwap, 80);
      });
      window.__fluxCartObserver.observe(document.body, { childList: true, subtree: true });
    }
    // Initial: na page load, fetch cart data en swap
    if (document.readyState === 'complete') swapCartPreviewImages();
    else window.addEventListener('load', () => setTimeout(swapCartPreviewImages, 200));

    // -------- Cart drawer opener (probeer meerdere methoden) --------
    function openCartDrawer() {
      // 1. Standaard Shopify events
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
      // 2. Custom element met open() methode
      const drawer = document.querySelector('cart-drawer-component, cart-drawer, #cart-drawer, [id*="cart-drawer"]');
      if (drawer) {
        if (typeof drawer.open === 'function') { drawer.open(); return; }
        if (typeof drawer.show === 'function') { drawer.show(); return; }
        // Details element pattern
        if (drawer.tagName === 'DETAILS') { drawer.open = true; return; }
      }
      // 3. Klik op cart-knop in header als laatste optie
      const cartBtn = document.querySelector(
        'header [aria-controls*="cart"], ' +
        'header a[href="/cart"], ' +
        'header [aria-label*="winkelwagen" i], ' +
        'header [aria-label*="cart" i]'
      );
      if (cartBtn) { cartBtn.click(); return; }
      // 4. Fallback — als drawer echt niet open gaat, dan toch redirect (na korte vertraging)
      setTimeout(() => { window.location.href = '/cart'; }, 800);
    }

    /* Canvas composite: AI result + hoekornament overlay → upload naar backend → URL */
    async function composeAndUpload(aiUrl, overlayUrl) {
      const size = cfg.outputSize || 2048;
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      const [aiImg, overlayImg] = await Promise.all([loadCorsImage(aiUrl), loadCorsImage(overlayUrl)]);
      ctx.drawImage(aiImg, 0, 0, size, size);
      ctx.drawImage(overlayImg, 0, 0, size, size);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      /* Upload naar backend upload-image endpoint — fallback: gebruik data URL als endpoint niet bestaat */
      const uploadUrl = root.dataset.uploadEndpoint || cfg.backendUrl.replace(/\/style-transfer$/, '/upload-image');
      try {
        const r = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_base64: dataUrl,
            widget_token: cfg.widgetToken,
            filename: 'composite-' + Date.now() + '.jpg'
          })
        });
        if (r.ok) {
          const data = await r.json();
          if (data.image_url) return data.image_url;
        }
      } catch {}
      /* Fallback: ruwe AI URL ipv composite (composite endpoint nog niet deployed) */
      return aiUrl;
    }
    function loadCorsImage(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image load failed: ' + url));
        img.src = url;
      });
    }

    // -------- Modal --------
    async function openCropperModal(file) {
      originalFile = file;
      const resized = await resizeImageBlob(file, cfg.maxMp);
      originalBlob = resized.blob;
      openModalWithBlob(originalBlob);
    }
    function openModalWithBlob(blob) {
      const url = URL.createObjectURL(blob);
      els.modalImg.onload = () => {
        URL.revokeObjectURL(url);
        els.modal.hidden = false;
        document.body.style.overflow = 'hidden';
        requestAnimationFrame(() => {
          cropper = new Cropper(els.modalCrop, els.modalImg);
        });
      };
      els.modalImg.onerror = () => {
        URL.revokeObjectURL(url);
        showError('Kon afbeelding niet laden.');
      };
      els.modalImg.src = url;
    }
    function closeModal() {
      els.modal.hidden = true;
      document.body.style.overflow = '';
      cropper = null;
    }
    async function confirmAndGenerate() {
      if (!cropper) return closeModal();
      try {
        croppedDataUrl = await cropper.toDataURL(cfg.outputSize, 0.95);
      } catch (err) {
        showError('Crop renderen mislukt: ' + err.message);
        return;
      }
      closeModal();
      if (cfg.mode === 'upload-only') startUploadOnlyFlow();
      else if (cfg.mode === 'valentijn') startValentijnFlow();
      else if (isMokLike) startMokFlow();
      else startGeneration();
    }

    // -------- Maps flow --------
    let mapsState = { lng: 4.9041, lat: 52.3676, zoom: 10, pinLng: null, pinLat: null, placeName: '', caption: '', captionManual: false };
    // Coords formatteren als DMS: 52°22'13"N 4°53'43"E
    function _toDMS(deg, isLat) {
      const abs = Math.abs(deg);
      const d = Math.floor(abs);
      const minF = (abs - d) * 60;
      const m = Math.floor(minF);
      const s = Math.round((minF - m) * 60);
      const dir = isLat ? (deg >= 0 ? 'N' : 'S') : (deg >= 0 ? 'E' : 'W');
      return d + '°' + m + "'" + s + '"' + dir;
    }
    function _buildAutoCaption() {
      if (mapsState.pinLng == null || mapsState.pinLat == null) return '';
      const shortName = (mapsState.placeName || '').split(',')[0].trim();
      const coords = _toDMS(mapsState.pinLat, true) + ' ' + _toDMS(mapsState.pinLng, false);
      return (shortName ? shortName + '\n' : '') + coords;
    }
    let mapsInstance = null;
    let mapsMarker = null;
    // Style ID mapping (klassiek/natuur/satelliet/minimaal)
    const MAPS_STYLES = {
      klassiek:  'mapbox/streets-v12',
      natuur:    'mapbox/outdoors-v12',
      satelliet: 'mapbox/satellite-streets-v12',
      minimaal:  'mapbox/light-v11'
    };
    let mapsStyleKey = 'klassiek';
    function showMapsFallback(msg) {
      if (!isMapsMode) return;
      const tile = root.querySelector('.flux-st__maps-tile');
      if (!tile) return;
      const fb = tile.querySelector('.flux-st__maps-fallback');
      const msgEl = tile.querySelector('.flux-st__maps-fallback-msg');
      const hint = tile.querySelector('.flux-st__maps-hint');
      if (msgEl && msg) msgEl.textContent = msg;
      if (fb) fb.hidden = false;
      if (hint) hint.style.display = 'none';
    }
    if (isMapsMode) {
      const tile = root.querySelector('.flux-st__maps-tile');
      if (tile) {
        mapsStyleKey = tile.dataset.style || 'klassiek';
        const token = tile.dataset.mapboxToken;
        if (!token) {
          // Setup melding — alleen in-tile fallback panel, NIET in customer error area
          showMapsFallback('Mapbox token ontbreekt — vul in theme editor in onder Foto Transformatie → Maps tool.');
          console.warn('[flux] Mapbox token ontbreekt');
        } else {
          // Polling — Mapbox CDN is deferred, may not be parsed yet
          function waitForMapbox(attempts) {
            attempts = attempts || 0;
            if (typeof window.mapboxgl !== 'undefined') return initMap();
            if (attempts > 100) {
              showMapsFallback('Mapbox kon niet geladen worden — check je internet / ad-blocker en herlaad de pagina.');
              return;
            }
            setTimeout(() => waitForMapbox(attempts + 1), 100);
          }
          function initMap() {
            try {
              window.mapboxgl.accessToken = token;
              const canvas = tile.querySelector('.flux-st__maps-canvas');
              mapsInstance = new window.mapboxgl.Map({
                container: canvas,
                style: 'mapbox://styles/' + MAPS_STYLES[mapsStyleKey],
                center: [mapsState.lng, mapsState.lat],
                zoom: mapsState.zoom,
                attributionControl: false
              });
              mapsInstance.addControl(new window.mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
              mapsInstance.on('move', () => {
                const c = mapsInstance.getCenter();
                mapsState.lng = c.lng;
                mapsState.lat = c.lat;
                mapsState.zoom = mapsInstance.getZoom();
              });
              mapsInstance.on('click', (e) => {
                // Klik op kaart = reset manualEdit zodat nieuwe locatie auto-vult
                mapsState.captionManual = false;
                mapsState.placeName = ''; // reverse-geocode hieronder vult 'm
                placePin(e.lngLat.lng, e.lngLat.lat);
                // Reverse-geocode coords → placeName voor caption
                _reverseGeocode(e.lngLat.lng, e.lngLat.lat);
              });
              mapsInstance.on('error', (e) => {
                console.error('[Mapbox]', e && e.error || e);
                const errMsg = (e && e.error && e.error.message) || '';
                if (errMsg.toLowerCase().indexOf('unauthorized') !== -1 || errMsg.indexOf('401') !== -1) {
                  showMapsFallback('Mapbox token werd geweigerd (401) — controleer de pk.* token in theme editor.');
                }
              });
              mapsInstance.on('load', () => {
                // Resize na load om correct in container te passen
                setTimeout(() => { try { mapsInstance.resize(); } catch (e) {} }, 50);
              });
              // ResizeObserver — bij layout changes (mobile rotate, drawer open) opnieuw resizen
              if (typeof ResizeObserver !== 'undefined') {
                new ResizeObserver(() => { try { mapsInstance.resize(); } catch (e) {} }).observe(canvas);
              }
              els.addCartBtn.disabled = true; // cart disabled tot pin geplaatst
            } catch (err) {
              console.error('[Mapbox] init failed', err);
              showMapsFallback('Mapbox init mislukt: ' + (err && err.message || err));
            }
          }
          waitForMapbox();
        }

        // Style switcher pills
        const styleBtns = root.querySelectorAll('.flux-st__maps-style');
        styleBtns.forEach(btn => {
          btn.addEventListener('click', () => {
            const key = btn.dataset.style;
            if (!key || !MAPS_STYLES[key]) return;
            mapsStyleKey = key;
            tile.dataset.style = key;
            styleBtns.forEach(b => b.classList.toggle('flux-st__maps-style--active', b === btn));
            if (mapsInstance) {
              try {
                mapsInstance.setStyle('mapbox://styles/' + MAPS_STYLES[key]);
                // setStyle removes markers — re-add pin if needed + thicken minimaal
                // Gebruik 'idle' (na alle tiles geladen) ipv 'styledata' (te vroeg + spammy)
                mapsInstance.once('idle', () => {
                  if (mapsState.pinLng != null && mapsState.pinLat != null) {
                    placePin(mapsState.pinLng, mapsState.pinLat);
                  }
                  _thickenMinimalLines();
                });
              } catch (e) { console.warn('setStyle failed', e); }
            }
          });
        });

        function placePin(lng, lat) {
          mapsState.pinLng = lng;
          mapsState.pinLat = lat;
          if (mapsMarker) mapsMarker.remove();
          // Custom red SVG marker
          const el = document.createElement('div');
          el.className = 'flux-maps-pin';
          el.innerHTML = '<svg viewBox="0 0 28 38" xmlns="http://www.w3.org/2000/svg"><path d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 24 14 24s14-13.5 14-24c0-7.73-6.27-14-14-14z" fill="#e23b3b" stroke="#fff" stroke-width="2"/><circle cx="14" cy="14" r="5" fill="#fff"/></svg>';
          mapsMarker = new window.mapboxgl.Marker({ element: el, anchor: 'bottom' })
            .setLngLat([lng, lat])
            .addTo(mapsInstance);
          tile.classList.add('has-pin');
          updateCoordsLabel();
          // Auto-fill caption tenzij user 'm handmatig heeft aangepast
          const capEl = root.querySelector('.flux-st__maps-caption');
          if (capEl && !mapsState.captionManual) {
            mapsState.caption = _buildAutoCaption();
            capEl.value = mapsState.caption;
          }
          // Sync overlay preview op tile
          if (typeof tile._updateCaptionPreview === 'function') tile._updateCaptionPreview();
          els.addCartBtn.disabled = false;
        }
        // Tile-overlay caption preview helper — toont tekst op de map tile in de tool
        function _updateCaptionPreview() {
          const prev = tile.querySelector('[data-caption-preview]');
          if (!prev) return;
          const cap = (mapsState.caption || '').trim();
          if (!cap) { prev.hidden = true; prev.innerHTML = ''; return; }
          const lines = cap.split('\n').map(l => l.trim()).filter(Boolean).slice(0, 3);
          let html = '';
          if (lines.length === 1) {
            html = '<span class="flux-st__maps-caption-title">' + lines[0] + '</span>';
          } else {
            html = '<span class="flux-st__maps-caption-title">' + lines[0].toUpperCase() + '</span>';
            for (let i = 1; i < lines.length; i++) {
              html += '<span class="flux-st__maps-caption-sub">' + lines[i] + '</span>';
            }
          }
          prev.innerHTML = html;
          prev.hidden = false;
        }
        // Textarea binding — markeer als manual zodra user typt + sync naar overlay
        const captionEl = root.querySelector('.flux-st__maps-caption');
        if (captionEl) {
          captionEl.addEventListener('input', () => {
            mapsState.caption = captionEl.value;
            mapsState.captionManual = true;
            _updateCaptionPreview();
          });
        }
        // Expose preview updater op de outer scope via tile dataset hook
        tile._updateCaptionPreview = _updateCaptionPreview;
        // Minimaal style: maak lijnen 2x dikker zodat kaart meer uitspringt
        // BELANGRIJK: alleen op 'style.load' event (eenmaal per style-switch),
        // NIET op 'styledata' want dat fired ook op elke setPaintProperty →
        // dat zou een infinite loop maken EN search/flyTo blokkeren.
        let _thickenGuard = false;
        function _thickenMinimalLines() {
          if (_thickenGuard) return;
          if (!mapsInstance || mapsStyleKey !== 'minimaal') return;
          _thickenGuard = true;
          let layers;
          try { layers = (mapsInstance.getStyle() || {}).layers || []; } catch (e) { _thickenGuard = false; return; }
          layers.forEach(layer => {
            if (layer.type !== 'line') return;
            const id = (layer.id || '').toLowerCase();
            if (!/road|street|motorway|highway|bridge|tunnel|link|trunk|path/.test(id)) return;
            try {
              const cur = mapsInstance.getPaintProperty(layer.id, 'line-width');
              if (typeof cur === 'number') {
                mapsInstance.setPaintProperty(layer.id, 'line-width', cur * 2);
              } else if (Array.isArray(cur)) {
                mapsInstance.setPaintProperty(layer.id, 'line-width', ['*', cur, 2]);
              }
            } catch (e) { /* skip layers that don't accept expressions */ }
          });
          _thickenGuard = false;
        }
        // Een eenmalige 'idle' run na de eerste keer dat de map klaar is met laden
        // V77: try/catch zodat een fout hier nooit de search-handler binding kan blokken
        try {
          mapsInstance.once('idle', () => {
            try { _thickenMinimalLines(); } catch (e) { console.warn('[Maps] thicken fail', e); }
            try { mapsInstance.resize(); } catch (e) {}
          });
        } catch (e) { console.warn('[Maps] idle hook fail', e); }

        function updateCoordsLabel() {
          const c = root.querySelector('.flux-st__maps-coords');
          if (!c) return;
          if (mapsState.pinLng == null) { c.innerHTML = '<em>Geen pin geplaatst</em>'; return; }
          const place = mapsState.placeName ? '<strong>' + mapsState.placeName + '</strong><br>' : '';
          c.innerHTML = place + 'Lat: ' + mapsState.pinLat.toFixed(5) + ' &middot; Lng: ' + mapsState.pinLng.toFixed(5);
        }

        // Search via Mapbox Geocoding API
        const searchInput = root.querySelector('.flux-st__maps-search');
        const searchBtn = root.querySelector('.flux-st__maps-search-btn');
        // Reverse-geocode helper: coords → placeName voor caption
        async function _reverseGeocode(lng, lat) {
          if (!mapsInstance) return;
          const token = tile.dataset.mapboxToken;
          if (!token) return;
          try {
            const url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
              encodeURIComponent(lng + ',' + lat) +
              '.json?access_token=' + token +
              '&types=place,locality,address,neighborhood&limit=1';
            const r = await fetch(url).then(r => r.json());
            const feat = r && r.features && r.features[0];
            if (!feat) return;
            // Korte naam = text (zoals 'Amsterdam') ipv volledige place_name
            mapsState.placeName = feat.text || feat.place_name || '';
            if (!mapsState.captionManual) {
              const capEl = root.querySelector('.flux-st__maps-caption');
              mapsState.caption = _buildAutoCaption();
              if (capEl) capEl.value = mapsState.caption;
              if (typeof tile._updateCaptionPreview === 'function') tile._updateCaptionPreview();
            }
            updateCoordsLabel();
          } catch (e) { /* fail silent — coords blijven dan zonder titel */ }
        }
        async function doSearch() {
          if (!searchInput || !mapsInstance) return;
          const q = searchInput.value.trim();
          if (!q) return;
          const token = tile.dataset.mapboxToken;
          if (!token) { alert('Mapbox token ontbreekt.'); return; }
          if (searchBtn) searchBtn.disabled = true;
          try {
            const url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' + encodeURIComponent(q) + '.json?access_token=' + token + '&country=nl,be&limit=1';
            const r = await fetch(url).then(r => r.json());
            const feat = r && r.features && r.features[0];
            if (!feat) { alert('Geen locatie gevonden voor "' + q + '"'); return; }
            const [lng, lat] = feat.center;
            // Korte titel ipv full address — feat.text = "Amsterdam", feat.place_name = "Amsterdam, NH, NL"
            mapsState.placeName = feat.text || (feat.place_name || q).split(',')[0].trim();
            mapsState.captionManual = false; // nieuwe locatie → auto-caption opnieuw
            mapsInstance.flyTo({ center: [lng, lat], zoom: 14, essential: true });
            placePin(lng, lat);
          } catch (err) {
            console.error('Geocode failed', err);
            alert('Zoeken mislukt: ' + (err.message || err));
          } finally {
            if (searchBtn) searchBtn.disabled = false;
          }
        }
        if (searchBtn) searchBtn.addEventListener('click', function (e) { e.preventDefault(); doSearch(); });
        if (searchInput) searchInput.addEventListener('keydown', function (e) {
          if (e.key === 'Enter') { e.preventDefault(); doSearch(); }
        });

        // Mijn locatie
        root.querySelector('.flux-st__maps-locate')?.addEventListener('click', () => {
          if (!navigator.geolocation || !mapsInstance) return;
          navigator.geolocation.getCurrentPosition(pos => {
            const { longitude, latitude } = pos.coords;
            mapsState.placeName = '';
            mapsState.captionManual = false;
            mapsInstance.flyTo({ center: [longitude, latitude], zoom: 14, essential: true });
            placePin(longitude, latitude);
            _reverseGeocode(longitude, latitude);
          }, () => { alert('Locatie kon niet bepaald worden.'); });
        });

        // Reset pin
        root.querySelector('.flux-st__maps-reset')?.addEventListener('click', () => {
          if (mapsMarker) { mapsMarker.remove(); mapsMarker = null; }
          mapsState.pinLng = null; mapsState.pinLat = null; mapsState.placeName = '';
          mapsState.caption = ''; mapsState.captionManual = false;
          const capEl = root.querySelector('.flux-st__maps-caption');
          if (capEl) capEl.value = '';
          if (typeof tile._updateCaptionPreview === 'function') tile._updateCaptionPreview();
          tile.classList.remove('has-pin');
          updateCoordsLabel();
          els.addCartBtn.disabled = true;
        });
      }
    }

    // Static image render voor cart preview + print bestand
    async function renderAndUploadMapsTile() {
      const tile = root.querySelector('.flux-st__maps-tile');
      const token = tile.dataset.mapboxToken;
      if (!mapsState.pinLng || !mapsState.pinLat) throw new Error('Plaats eerst een pin op de kaart.');
      const size = Math.min(1280, cfg.outputSize || 1280); // Mapbox static max = 1280
      // pin-l: large red pin marker at the user's lat/lng
      // auto: bbox auto-fit; we use center + zoom instead for predictable result
      const lng = mapsState.lng.toFixed(6);
      const lat = mapsState.lat.toFixed(6);
      const zoom = Math.min(20, Math.max(0, mapsState.zoom)).toFixed(2);
      const pin = 'pin-l+e23b3b(' + mapsState.pinLng.toFixed(6) + ',' + mapsState.pinLat.toFixed(6) + ')';
      const styleSlug = MAPS_STYLES[mapsStyleKey] || MAPS_STYLES.klassiek;
      const staticUrl = 'https://api.mapbox.com/styles/v1/' + styleSlug + '/static/' +
        pin + '/' + lng + ',' + lat + ',' + zoom + '/' + size + 'x' + size + '@2x?access_token=' + token;
      // Fetch image → upload naar Vercel Blob zodat token niet in cart property komt
      const imgRes = await fetch(staticUrl);
      if (!imgRes.ok) throw new Error('Mapbox static API ' + imgRes.status);
      const blob = await imgRes.blob();
      // Caption overlay: teken kaart + tekstbalk op een canvas
      const mapImg = await new Promise((resolve, reject) => {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = URL.createObjectURL(blob);
      });
      const cap = (mapsState.caption || '').trim();
      const finalSize = size * 2; // Mapbox @2x = 2560px max
      const canvas = document.createElement('canvas');
      canvas.width = finalSize;
      canvas.height = finalSize;
      const ctx = canvas.getContext('2d');
      // Witte achtergrond voor caption-balk fallback
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // Map vult bovenste deel
      const mapHeight = cap ? Math.round(finalSize * 0.82) : finalSize;
      ctx.drawImage(mapImg, 0, 0, finalSize, mapHeight);
      if (cap) {
        // Tekstbalk onderaan
        const barY = mapHeight;
        const barH = finalSize - barY;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, barY, finalSize, barH);
        // Tekst centreren — eerste regel groot, tweede regel kleiner (coords look)
        const lines = cap.split('\n').map(s => s.trim()).filter(Boolean).slice(0, 3);
        ctx.fillStyle = '#1a1a1a';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const cx = finalSize / 2;
        if (lines.length === 1) {
          ctx.font = '700 ' + Math.round(barH * 0.32) + 'px "Cinzel", Georgia, serif';
          ctx.fillText(lines[0], cx, barY + barH / 2);
        } else {
          // Regel 1 = titel (groot), overige = coords/sub (kleiner)
          const titleSize = Math.round(barH * 0.30);
          const subSize = Math.round(barH * 0.16);
          const totalH = titleSize + (lines.length - 1) * subSize * 1.4;
          let y = barY + (barH - totalH) / 2 + titleSize / 2;
          ctx.font = '700 ' + titleSize + 'px "Cinzel", Georgia, serif';
          ctx.fillText(lines[0].toUpperCase(), cx, y);
          y += titleSize * 0.55;
          ctx.font = '500 ' + subSize + 'px Georgia, "Times New Roman", serif';
          ctx.fillStyle = '#3a3a3a';
          for (let i = 1; i < lines.length; i++) {
            y += subSize * 1.4;
            ctx.fillText(lines[i], cx, y);
          }
        }
      }
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      return uploadDataUrl(dataUrl, 'maps-' + Date.now() + '.jpg');
    }

    // -------- Mok flow (foto op mok mockup met optionele tekst) --------
    let currentMokColor = 'wit';
    let currentMokColorUrl = '';
    let currentMokColorLabel = 'Wit';
    let mokPhotoPos = { x: 50, y: 50 }; // % van tile, draggable
    let mokPhotoSize = 'middel';        // klein/middel/groot (preset)
    let customPhotoWidthPct = null;     // null = use preset, anders custom % via resize handle
    // T-shirt side state: 2 onafhankelijke ontwerpen (voorkant + achterkant)
    function makeEmptySide() {
      return {
        photoUrl: null,         // croppedDataUrl voor deze zijde
        originalFile: null,     // originele file (voor _originele_filename property)
        photoPos: { x: 50, y: 50 },
        photoSize: 'middel',
        customWidthPct: null,
        textContent: '',
        textPos: { x: 50, y: 50 },
        textSize: 'middel',
        textColor: '#ffffff',
      };
    }
    let tshirtSides = { front: makeEmptySide(), back: makeEmptySide() };
    let currentTshirtSide = 'front';
    function startMokFlow() {
      hideError();
      resultUrl = null;
      const photoEl = root.querySelector('.flux-st__mok-photo');
      const photoWrap = root.querySelector('.flux-st__mok-photo-wrap');
      const uploadCta = root.querySelector('.flux-st__mok-upload-cta');
      if (photoEl) { photoEl.src = croppedDataUrl; photoEl.hidden = false; }
      if (photoWrap) photoWrap.hidden = false;
      if (uploadCta) uploadCta.hidden = true;
      els.postActions.hidden = false;
      els.addCartBtn.disabled = false;
      // Toon draggable text overlay (sibling van photo-wrap) als die bestaat
      const overlay = root.querySelector('.flux-st__text-overlay--draggable');
      if (overlay) overlay.hidden = false;
    }
    if (isMokLike) {
      // Tekst overlay direct zichtbaar (los van photo upload state)
      const mokOverlay = root.querySelector('.flux-st__text-overlay--draggable');
      if (mokOverlay) mokOverlay.hidden = false;
      // Mockup img — verschilt per mode: mok = .flux-st__mok-mockup, tshirt = .flux-st__tshirt-mockup
      const mockupImg = root.querySelector(isTshirtMode ? '.flux-st__tshirt-mockup' : '.flux-st__mok-mockup');
      // Radios verschillen per mode: mok = flux-st-mok-color-*, tshirt = flux-st-tshirt-variant-*
      const radioNamePrefix = isTshirtMode ? 'flux-st-tshirt-variant-' : 'flux-st-mok-color-';
      const variantCardClass = isTshirtMode ? 'flux-st__tshirt-variant' : 'flux-st__mok-color';
      const colorRadios = root.querySelectorAll('input[type=radio][name^="' + radioNamePrefix + '"]');
      const initial = root.querySelector('input[type=radio][name^="' + radioNamePrefix + '"]:checked');
      if (initial) {
        currentMokColor = initial.value;
        currentMokColorUrl = initial.dataset.mokUrl || (mockupImg && mockupImg.src) || '';
        currentMokColorLabel = initial.dataset.mokLabel || (isTshirtMode ? 'Standaard' : 'Wit');
      } else if (mockupImg) {
        currentMokColorUrl = mockupImg.src;
      }
      colorRadios.forEach(input => {
        input.addEventListener('change', (e) => {
          currentMokColor = e.target.value;
          currentMokColorUrl = e.target.dataset.mokUrl || '';
          currentMokColorLabel = e.target.dataset.mokLabel || currentMokColor;
          // BELANGRIJK: ook variant ID updaten zodat cart de juiste Shopify variant gebruikt
          const vid = parseInt(e.target.dataset.variantId, 10);
          if (vid) currentVariantId = vid;
          if (mockupImg && currentMokColorUrl) mockupImg.src = currentMokColorUrl;
          root.querySelectorAll('.' + variantCardClass).forEach(el => el.classList.remove(variantCardClass + '--checked'));
          input.closest('.' + variantCardClass)?.classList.add(variantCardClass + '--checked');
        });
      });

      // -------- Foto draggable + resize --------
      const photoWrap = root.querySelector('.flux-st__mok-photo-wrap');
      const photoSizeInputs = root.querySelectorAll('input[type=radio][name^="flux-st-mok-photo-size-"]');

      if (photoWrap) {
        let pDragging = false;
        let pStartX = 0, pStartY = 0, pStartPosX = 50, pStartPosY = 50;
        photoWrap.addEventListener('pointerdown', (e) => {
          if (photoWrap.hidden) return;
          pDragging = true;
          photoWrap.classList.add('is-dragging');
          try { photoWrap.setPointerCapture(e.pointerId); } catch {}
          pStartX = e.clientX; pStartY = e.clientY;
          pStartPosX = mokPhotoPos.x; pStartPosY = mokPhotoPos.y;
          e.preventDefault();
        });
        photoWrap.addEventListener('pointermove', (e) => {
          if (!pDragging) return;
          const tile = root.querySelector('.flux-st__tile');
          if (!tile) return;
          const rect = tile.getBoundingClientRect();
          if (!rect.width) return;
          const dxP = ((e.clientX - pStartX) / rect.width) * 100;
          const dyP = ((e.clientY - pStartY) / rect.height) * 100;
          mokPhotoPos.x = Math.max(5, Math.min(95, pStartPosX + dxP));
          mokPhotoPos.y = Math.max(5, Math.min(95, pStartPosY + dyP));
          photoWrap.style.left = mokPhotoPos.x + '%';
          photoWrap.style.top = mokPhotoPos.y + '%';
          photoWrap.dataset.x = mokPhotoPos.x.toFixed(1);
          photoWrap.dataset.y = mokPhotoPos.y.toFixed(1);
        });
        const stopPDrag = () => { pDragging = false; photoWrap.classList.remove('is-dragging'); };
        photoWrap.addEventListener('pointerup', stopPDrag);
        photoWrap.addEventListener('pointercancel', stopPDrag);
      }

      photoSizeInputs.forEach(input => {
        input.addEventListener('change', (e) => {
          mokPhotoSize = e.target.value;
          customPhotoWidthPct = null; // pill keuze reset custom resize
          if (photoWrap) {
            photoWrap.dataset.photoSize = mokPhotoSize;
            photoWrap.style.width = ''; // reset inline width naar CSS preset
          }
          syncPillChecked(photoSizeInputs, e.target);
        });
      });

      // -------- Resize handle (rechtsonder van foto-wrap) --------
      const resizeHandle = photoWrap ? photoWrap.querySelector('.flux-st__resize-handle') : null;
      if (resizeHandle && photoWrap) {
        let rDragging = false;
        let rStartX = 0, rStartY = 0;
        let rStartWidthPct = 0;
        let rCenterX = 0, rCenterY = 0; // center van wrap op moment van pointerdown
        let rTileRect = null;
        resizeHandle.addEventListener('pointerdown', (e) => {
          e.stopPropagation(); // niet trigger photo drag
          rDragging = true;
          resizeHandle.classList.add('is-resizing');
          try { resizeHandle.setPointerCapture(e.pointerId); } catch {}
          const tile = root.querySelector('.flux-st__tile');
          if (!tile) return;
          rTileRect = tile.getBoundingClientRect();
          const wrapRect = photoWrap.getBoundingClientRect();
          rCenterX = wrapRect.left + wrapRect.width / 2;
          rCenterY = wrapRect.top + wrapRect.height / 2;
          rStartX = e.clientX;
          rStartY = e.clientY;
          // Bepaal huidige width %: gebruik inline style indien aanwezig anders preset map
          if (customPhotoWidthPct != null) {
            rStartWidthPct = customPhotoWidthPct;
          } else {
            const presetMap = isTshirtMode
              ? { klein: 40, middel: 55, groot: 75 }
              : { klein: 26, middel: 38, groot: 54 };
            rStartWidthPct = presetMap[mokPhotoSize] || 50;
          }
          e.preventDefault();
        });
        resizeHandle.addEventListener('pointermove', (e) => {
          if (!rDragging || !rTileRect) return;
          // Bereken nieuwe radius vanaf centerpunt; alleen X+Y richting naar rechtsonder telt
          const startDist = Math.hypot(rStartX - rCenterX, rStartY - rCenterY);
          const newDist = Math.hypot(e.clientX - rCenterX, e.clientY - rCenterY);
          if (startDist < 4) return;
          const scale = newDist / startDist;
          const newWidthPct = Math.max(5, Math.min(95, rStartWidthPct * scale));
          customPhotoWidthPct = newWidthPct;
          photoWrap.style.width = newWidthPct.toFixed(2) + '%';
        });
        const stopResize = () => {
          rDragging = false;
          resizeHandle.classList.remove('is-resizing');
        };
        resizeHandle.addEventListener('pointerup', stopResize);
        resizeHandle.addEventListener('pointercancel', stopResize);
      }

      // -------- T-shirt: Voor/Achter tabs + achterkant checkbox --------
      if (isTshirtMode) {
        const sideTabs = root.querySelectorAll('.flux-st__tshirt-side-tab');
        const achterCheck = root.querySelector('.flux-st__achter-check');

        function saveCurrentSide() {
          const s = tshirtSides[currentTshirtSide];
          s.photoUrl = croppedDataUrl;
          s.originalFile = originalFile;
          s.photoPos = { ...mokPhotoPos };
          s.photoSize = mokPhotoSize;
          s.customWidthPct = customPhotoWidthPct;
          s.textContent = currentTextContent;
          s.textPos = { ...uploadTextPos };
          s.textSize = currentTextSize;
          s.textColor = currentTextColor;
        }
        function loadSide(side) {
          const s = tshirtSides[side];
          croppedDataUrl = s.photoUrl;
          originalFile = s.originalFile;
          mokPhotoPos = { ...s.photoPos };
          mokPhotoSize = s.photoSize;
          customPhotoWidthPct = s.customWidthPct;
          currentTextContent = s.textContent;
          uploadTextPos = { ...s.textPos };
          currentTextSize = s.textSize;
          currentTextColor = s.textColor;

          // Update photo UI
          const photoEl = root.querySelector('.flux-st__mok-photo');
          const photoWrap2 = root.querySelector('.flux-st__mok-photo-wrap');
          const uploadCta = root.querySelector('.flux-st__mok-upload-cta');
          if (croppedDataUrl) {
            if (photoEl) { photoEl.src = croppedDataUrl; photoEl.hidden = false; }
            if (photoWrap2) {
              photoWrap2.hidden = false;
              photoWrap2.style.left = mokPhotoPos.x + '%';
              photoWrap2.style.top = mokPhotoPos.y + '%';
              photoWrap2.dataset.x = mokPhotoPos.x.toFixed(1);
              photoWrap2.dataset.y = mokPhotoPos.y.toFixed(1);
              photoWrap2.dataset.photoSize = mokPhotoSize;
              photoWrap2.style.width = customPhotoWidthPct != null ? customPhotoWidthPct.toFixed(2) + '%' : '';
            }
            if (uploadCta) uploadCta.hidden = true;
          } else {
            if (photoEl) photoEl.hidden = true;
            if (photoWrap2) photoWrap2.hidden = true;
            if (uploadCta) uploadCta.hidden = false;
          }

          // Update text UI
          if (els.textInput) els.textInput.value = currentTextContent;
          if (els.textContent) {
            els.textContent.textContent = currentTextContent || '';
            els.textContent.classList.toggle('flux-st__text-content--placeholder', !currentTextContent);
          }
          if (els.textOverlay) {
            els.textOverlay.style.left = uploadTextPos.x + '%';
            els.textOverlay.style.top = uploadTextPos.y + '%';
            els.textOverlay.dataset.x = uploadTextPos.x.toFixed(1);
            els.textOverlay.dataset.y = uploadTextPos.y.toFixed(1);
            els.textOverlay.style.color = currentTextColor;
            els.textOverlay.dataset.size = currentTextSize;
          }
          // Sync radio pills
          const textSizeInputs = root.querySelectorAll('input[type=radio][name^="flux-st-text-size-"]');
          textSizeInputs.forEach(r => { r.checked = (r.value === currentTextSize); });
          const photoSizeInputs2 = root.querySelectorAll('input[type=radio][name^="flux-st-mok-photo-size-"]');
          photoSizeInputs2.forEach(r => { r.checked = (r.value === mokPhotoSize); });
        }
        function switchSide(newSide) {
          if (newSide === currentTshirtSide) return;
          saveCurrentSide();
          currentTshirtSide = newSide;
          loadSide(newSide);
          sideTabs.forEach(t => {
            const isActive = t.dataset.side === newSide;
            t.classList.toggle('flux-st__tshirt-side-tab--active', isActive);
            t.setAttribute('aria-selected', isActive ? 'true' : 'false');
          });
          // Wanneer user voor het eerst op Achterkant klikt: auto-check de checkbox
          if (newSide === 'back' && achterCheck && !achterCheck.checked) {
            achterCheck.checked = true;
            achterCheck.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        sideTabs.forEach(tab => {
          tab.addEventListener('click', () => switchSide(tab.dataset.side));
        });
        // Achterkant checkbox visueel updaten op tab badge
        function updateAchterBadge() {
          const backTab = root.querySelector('.flux-st__tshirt-side-tab[data-side="back"]');
          if (backTab) backTab.classList.toggle('flux-st__tshirt-side-tab--enabled', achterCheck && achterCheck.checked);
        }
        if (achterCheck) {
          achterCheck.addEventListener('change', updateAchterBadge);
        }
      }
    }

    // -------- Render Mok tegel (mockup + foto in print area + optionele tekst) --------
    // withMockup=true → preview met mok mockup achtergrond
    // withMockup=false → print file: witte vierkante achtergrond met alleen foto+tekst
    // state (optioneel) → object met photoUrl/photoPos/photoSize/customWidthPct/textContent/textPos/textSize/textColor
    //   wordt gebruikt voor o.a. achterkant render zonder UI swap
    async function renderMokCanvas(withMockup, state) {
      // Default state = huidige working vars
      const st = state || {
        photoUrl: croppedDataUrl,
        photoPos: mokPhotoPos,
        photoSize: mokPhotoSize,
        customWidthPct: customPhotoWidthPct,
        textContent: currentTextContent,
        textPos: uploadTextPos,
        textSize: currentTextSize,
        textColor: currentTextColor,
      };
      const size = cfg.outputSize || 2048;
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      // 1. Mockup (alleen in preview)
      if (withMockup) {
        try {
          const mockupImg = await loadCorsImage(currentMokColorUrl);
          const mockScale = Math.min(size / mockupImg.width, size / mockupImg.height);
          const mw = mockupImg.width * mockScale;
          const mh = mockupImg.height * mockScale;
          ctx.drawImage(mockupImg, (size - mw) / 2, (size - mh) / 2, mw, mh);
        } catch (e) { console.warn('mockup load failed', e); }
      }
      // 2. Foto — in preview op user pos/size, in print file 1.5x groter en gecentreerd
      // T-shirt heeft volledige voorkant als print area → grotere foto pcts
      const photoSizeMap = isTshirtMode
        ? { klein: 0.40, middel: 0.55, groot: 0.75 }
        : { klein: 0.26, middel: 0.38, groot: 0.54 };
      // Custom resize via hoek handle overschrijft preset
      const previewPct = st.customWidthPct != null
        ? st.customWidthPct / 100
        : (photoSizeMap[st.photoSize] || photoSizeMap.middel);
      // Print file scaled up zodat de foto meer ruimte in het print kader gebruikt
      const photoSizePct = withMockup ? previewPct : Math.min(0.95, previewPct * 1.5);
      const photoSide = size * photoSizePct;
      // Preview gebruikt user-positie; print file altijd gecentreerd
      const photoCX = withMockup ? size * (st.photoPos.x / 100) : size / 2;
      const photoCY = withMockup ? size * (st.photoPos.y / 100) : size / 2;
      const photoX = photoCX - photoSide / 2;
      const photoY = photoCY - photoSide / 2;
      // Foto (alleen renderen als er een photoUrl is — bij text-only back side wordt foto geskipt)
      if (st.photoUrl) {
        const photoImg = await loadImageDataUrl(st.photoUrl);
        const photoScale = Math.max(photoSide / photoImg.width, photoSide / photoImg.height);
        const drawW = photoImg.width * photoScale;
        const drawH = photoImg.height * photoScale;
        const drawX = photoX + (photoSide - drawW) / 2;
        const drawY = photoY + (photoSide - drawH) / 2;
        ctx.save();
        ctx.beginPath();
        ctx.rect(photoX, photoY, photoSide, photoSide);
        ctx.clip();
        ctx.drawImage(photoImg, drawX, drawY, drawW, drawH);
        ctx.restore();
      }
      // 3. Optionele tekst overlay (onafhankelijk van foto positie — tekst-positie % is van tile)
      if (st.textContent) {
        const sizeMult = st.textSize === 'klein' ? 0.7 : st.textSize === 'groot' ? 1.4 : 1.0;
        const baseFontSize = size * 0.05 * sizeMult;
        ctx.fillStyle = st.textColor || '#ffffff';
        ctx.font = `700 ${baseFontSize}px "Cormorant Garamond", Georgia, serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(0,0,0,0.45)';
        ctx.shadowBlur = size * 0.005;
        ctx.shadowOffsetY = size * 0.0025;
        const maxWidth = size * 0.6;
        const lines = wrapText(ctx, st.textContent, maxWidth);
        const lineHeight = baseFontSize * 1.15;
        const totalHeight = lines.length * lineHeight;
        const cx = size * (st.textPos.x / 100);
        const cy = size * (st.textPos.y / 100);
        const startY = cy - totalHeight / 2 + lineHeight / 2;
        lines.forEach((line, i) => {
          ctx.fillText(line, cx, startY + i * lineHeight);
        });
        ctx.shadowColor = 'transparent';
      }
      return canvas.toDataURL('image/jpeg', 0.92);
    }
    // Wrapper voor de oude calls — preview met mockup (optional state voor achterkant)
    async function renderAndUploadMokTile(state, sideTag) {
      const dataUrl = await renderMokCanvas(true, state);
      const prefix = isTshirtMode ? 'tshirt-preview-' : 'mok-preview-';
      const tag = sideTag ? sideTag + '-' : '';
      return uploadDataUrl(dataUrl, prefix + tag + currentMokColor + '-' + Date.now() + '.jpg');
    }
    // Print file — alleen foto + tekst op witte achtergrond
    async function renderAndUploadMokPrintFile(state, sideTag) {
      const dataUrl = await renderMokCanvas(false, state);
      const prefix = isTshirtMode ? 'tshirt-print-' : 'mok-print-';
      const tag = sideTag ? sideTag + '-' : '';
      return uploadDataUrl(dataUrl, prefix + tag + currentMokColor + '-' + Date.now() + '.jpg');
    }

    // -------- Valentijn flow --------
    let valentijnNames = '';
    let valentijnDate = '';
    function startValentijnFlow() {
      hideError();
      resultUrl = null;
      const photoEl = root.querySelector('.flux-st__val-photo');
      const uploadCta = root.querySelector('.flux-st__val-upload-cta');
      if (photoEl) { photoEl.src = croppedDataUrl; photoEl.hidden = false; }
      if (uploadCta) uploadCta.hidden = true;
      // post-actions (Andere foto / Recrop) zichtbaar zodat user opnieuw kan
      els.postActions.hidden = false;
      els.addCartBtn.disabled = false;
    }
    if (isValMode) {
      const namesInput = root.querySelector('.flux-st__val-names');
      const dateInput = root.querySelector('.flux-st__val-date');
      const namesPreview = root.querySelector('.flux-st__val-name-preview');
      const datePreview = root.querySelector('.flux-st__val-date-preview');
      if (namesInput) {
        namesInput.addEventListener('input', (e) => {
          valentijnNames = e.target.value.trim();
          if (namesPreview) {
            namesPreview.textContent = valentijnNames || 'Naam 1 & Naam 2';
            namesPreview.classList.toggle('flux-st__val-name-preview--placeholder', !valentijnNames);
          }
        });
      }
      if (dateInput) {
        dateInput.addEventListener('input', (e) => {
          valentijnDate = e.target.value.trim();
          if (datePreview) {
            datePreview.textContent = valentijnDate || '25-06-2025';
            datePreview.classList.toggle('flux-st__val-date-preview--placeholder', !valentijnDate);
          }
        });
      }
      // Initial placeholder state
      if (namesPreview) namesPreview.classList.add('flux-st__val-name-preview--placeholder');
      if (datePreview) datePreview.classList.add('flux-st__val-date-preview--placeholder');
    }

    // -------- Render AI image + text overlay composite --------
    async function renderAndUploadAiWithText() {
      const size = cfg.outputSize || 2048;
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      const aiImg = await loadCorsImage(resultUrl);
      ctx.drawImage(aiImg, 0, 0, size, size);
      drawTextOverlayCanvas(ctx, size);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      return uploadDataUrl(dataUrl, 'ai-text-' + Date.now() + '.jpg');
    }

    // -------- Render Valentijn tegel (square foto top + namen + datum) --------
    async function renderAndUploadValentijnTile() {
      const size = cfg.outputSize || 2048;
      const canvas = document.createElement('canvas');
      canvas.width = size; canvas.height = size;
      const ctx = canvas.getContext('2d');
      // White polaroid bg
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      // Square photo area — 78% wide centered, top 4% padding (matches CSS preview)
      const photoSide = size * 0.78;
      const photoX = (size - photoSide) / 2;
      const photoY = size * 0.04;
      const photoImg = await loadImageDataUrl(croppedDataUrl);
      // Cropper outputs square → 1:1 mapping, geen distortie
      const scale = Math.max(photoSide / photoImg.width, photoSide / photoImg.height);
      const drawW = photoImg.width * scale;
      const drawH = photoImg.height * scale;
      const drawX = photoX + (photoSide - drawW) / 2;
      const drawY = photoY + (photoSide - drawH) / 2;
      ctx.save();
      ctx.beginPath();
      ctx.rect(photoX, photoY, photoSide, photoSide);
      ctx.clip();
      ctx.drawImage(photoImg, drawX, drawY, drawW, drawH);
      ctx.restore();
      // Text area (onderkant)
      const textY = photoY + photoSide + size * 0.025;
      const textH = size - textY - size * 0.04;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      if (valentijnNames) {
        ctx.fillStyle = '#1a1a1a';
        ctx.font = `italic 600 ${Math.round(size * 0.078)}px "Cormorant Garamond", Georgia, serif`;
        ctx.fillText(valentijnNames, size / 2, textY + textH * 0.35);
      }
      if (valentijnDate) {
        ctx.fillStyle = '#555555';
        ctx.font = `italic 500 ${Math.round(size * 0.046)}px "Cormorant Garamond", Georgia, serif`;
        ctx.fillText(valentijnDate, size / 2, textY + textH * 0.78);
      }
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      return uploadDataUrl(dataUrl, 'valentijn-' + Date.now() + '.jpg');
    }

    // -------- Helpers (gedeeld) --------
    function drawTextOverlayCanvas(ctx, size) {
      const text = (currentTextContent || '').trim();
      if (!text) return;
      ctx.fillStyle = currentTextColor || '#ffffff';
      ctx.font = `700 ${textPxSize(size, currentTextSize)}px "Cormorant Garamond", Georgia, serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'rgba(0,0,0,0.45)';
      ctx.shadowBlur = size * 0.005;
      ctx.shadowOffsetY = size * 0.0025;
      const maxWidth = size * 0.7;
      const lines = wrapText(ctx, text, maxWidth);
      const lineHeight = textPxSize(size, currentTextSize) * 1.15;
      const totalHeight = lines.length * lineHeight;
      const cx = size * (uploadTextPos.x / 100);
      const cy = size * (uploadTextPos.y / 100);
      const startY = cy - totalHeight / 2 + lineHeight / 2;
      lines.forEach((line, i) => {
        ctx.fillText(line, cx, startY + i * lineHeight);
      });
      ctx.shadowColor = 'transparent';
    }
    async function uploadDataUrl(dataUrl, filename) {
      const uploadUrl = root.dataset.uploadEndpoint || cfg.backendUrl.replace(/\/style-transfer$/, '/upload-image');
      const r = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image_base64: dataUrl, widget_token: cfg.widgetToken, filename })
      });
      if (!r.ok) throw new Error('Upload status ' + r.status);
      const data = await r.json();
      if (!data.image_url) throw new Error('Geen image URL ontvangen.');
      return data.image_url;
    }

    // -------- Klantfoto (origineel) — bewaar de door de klant aangeleverde bronfoto --------
    // Upload de originele (gecropte) klantfoto één keer naar Vercel Blob en cache
    // het resultaat, zodat we bij ELKE order een permanente link naar de bronfoto
    // hebben. Best-effort: faalt de upload, dan geven we null terug en slaan we de
    // property over — de tool en 'toevoegen aan winkelwagen' blijven altijd werken.
    let _customerPhotoCache = { data: null, url: null };
    async function ensureCustomerPhotoUrl(dataUrl) {
      if (!dataUrl || typeof dataUrl !== 'string' || dataUrl.indexOf('data:') !== 0) return null;
      if (_customerPhotoCache.data === dataUrl && _customerPhotoCache.url) return _customerPhotoCache.url;
      try {
        const url = await uploadDataUrl(dataUrl, 'klantfoto-' + Date.now() + '.jpg');
        _customerPhotoCache = { data: dataUrl, url };
        return url;
      } catch (e) {
        console.warn('[klantfoto] upload mislukt, property overgeslagen', e);
        return null;
      }
    }

    // -------- Upload-only flow — toon foto direct, composite + upload pas bij cart --------
    function startUploadOnlyFlow() {
      hideError();
      resultUrl = null; // wordt set bij cart click
      els.error.style.background = '';
      els.error.style.color = '';
      els.result.src = croppedDataUrl;
      loadImage(els.result, croppedDataUrl).then(() => {
        setState('result');
        els.postActions.hidden = false;
        // Toon text overlay (was hidden) zodat klant tekst kan toevoegen op de foto
        const overlay = root.querySelector('.flux-st__text-overlay--draggable');
        if (overlay) overlay.hidden = false;
        els.addCartBtn.disabled = false;
      }).catch((err) => {
        showError('Foto kon niet geladen worden: ' + err.message);
      });
    }

    // -------- Generation + progress --------
    async function startGeneration() {
      hideError();
      // Stap 1: zet cropped foto in preview EN switch state — gebeurt synchroon
      els.croppedPreview.src = croppedDataUrl;
      setState('generating');
      els.postActions.hidden = true;
      els.addCartBtn.disabled = true;
      setProgress(0);

      // Forceer een paint vóór de fetch begint, zodat de progress bar gegarandeerd zichtbaar is
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      // Simuleer linear progress 0 → 92% over 22 sec (FLUX 1 Kontext ~15-25s)
      const startTime = Date.now();
      const expectedDuration = 22_000;
      progressTimer = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const pct = Math.min(92, (elapsed / expectedDuration) * 92);
        setProgress(pct);
      }, 150);

      try {
        // CRITICAL: Vercel serverless functions hebben 4.5 MB request body limit
        // (door Vercel enforced ongeacht bodyParser config). Onze cropper output
        // is 2048×2048 JPEG = vaak 2-4 MB binary, na base64 → 3-5 MB → REJECTED
        // door Vercel met 413 BEFORE de function runt → geen log entry.
        // FIX: downscale naar max 1024×1024 JPEG @ 0.85 voor AI request payload.
        // FLUX/OpenAI image models downsamplen toch naar 1024 internally → geen
        // kwaliteitsverlies. De originele 2048 cropped versie blijft in
        // croppedDataUrl voor de cart preview upload (die via blob URL gaat).
        const aiInputDataUrl = await downscaleForAi(croppedDataUrl, 1024, 0.85);
        console.log('[flux] AI input size:', Math.round(aiInputDataUrl.length / 1024) + 'KB (base64)');
        const res = await fetch(cfg.backendUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            image_base64: aiInputDataUrl,
            prompt: cfg.prompt,
            shop: cfg.shopDomain,
            widget_token: cfg.widgetToken,
            provider: cfg.provider,
          }),
        });
        if (!res.ok) {
          let detail = `Backend gaf status ${res.status}`;
          try {
            const errBody = await res.json();
            detail = errBody.error || errBody.message || detail;
          } catch {}
          throw new Error(detail);
        }
        const data = await res.json();
        resultUrl = data.image_url || data.preview_url;
        if (!resultUrl) throw new Error('Geen image URL ontvangen.');

        // 92% → 98% terwijl image laadt
        setProgress(96);
        await loadImage(els.result, resultUrl);
        clearInterval(progressTimer);
        progressTimer = null;
        setProgress(100);
        // Korte pauze zodat 100% even zichtbaar is
        await new Promise((r) => setTimeout(r, 350));
        setState('result');
        els.postActions.hidden = false;
        els.addCartBtn.disabled = false;
      } catch (err) {
        if (progressTimer) { clearInterval(progressTimer); progressTimer = null; }
        // Log volledige error naar console voor diagnose (NIET naar klant tonen)
        console.error('[flux] AI generation failed:', err, err.message);
        // Vriendelijke melding voor klant
        showError('De AI is even niet bereikbaar. Probeer het zo opnieuw, of klik "Andere foto" om opnieuw te beginnen.');
        // BEHOUD de foto in beeld — geen reset naar empty. Toon postActions zodat
        // klant op "Recrop" of "Andere foto" kan klikken om opnieuw te proberen.
        if (els.croppedPreview) els.croppedPreview.hidden = false;
        if (els.generating) els.generating.hidden = true; // hide progress bar
        if (els.postActions) els.postActions.hidden = false;
        // Zet retry: klant kan opnieuw genereren door op een nieuwe knop te klikken
        if (els.addCartBtn) {
          els.addCartBtn.disabled = false;
          els.addCartBtn.textContent = 'Probeer opnieuw te genereren';
          // Tijdelijke retry binding — clean up oude listener eerst
          const oldHandler = els.addCartBtn._retryHandler;
          if (oldHandler) els.addCartBtn.removeEventListener('click', oldHandler);
          const retryHandler = (e) => {
            e.preventDefault();
            e.stopPropagation();
            els.addCartBtn.removeEventListener('click', retryHandler);
            els.addCartBtn._retryHandler = null;
            els.addCartBtn.textContent = originalCartLabel || 'Voeg toe aan winkelwagen';
            startGeneration(); // retry
          };
          els.addCartBtn._retryHandler = retryHandler;
          els.addCartBtn.addEventListener('click', retryHandler, { once: true });
        }
      }
    }
    function setProgress(pct) {
      const v = Math.round(pct);
      els.progressBar.style.width = v + '%';
      els.progressText.textContent = v + '%';
    }

    // Helper: downscale een data URL naar een max grootte JPEG voor AI backend
    // Voorkomt 413 Payload Too Large op Vercel (4.5 MB request limit).
    async function downscaleForAi(dataUrl, maxSide, quality) {
      maxSide = maxSide || 1024;
      quality = quality || 0.85;
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = reject;
        i.src = dataUrl;
      });
      // Skip resize als al klein genoeg
      if (img.width <= maxSide && img.height <= maxSide && dataUrl.length < 1_500_000) {
        return dataUrl;
      }
      const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, w, h);
      return canvas.toDataURL('image/jpeg', quality);
    }

    // -------- helpers --------
    function showError(msg) { els.error.textContent = msg; els.error.hidden = false; }
    function hideError() { els.error.hidden = true; els.error.textContent = ''; }
    function syncPillChecked(inputs, checked) {
      inputs.forEach((i) => {
        const pill = i.closest('.flux-st__pill');
        if (pill) pill.classList.toggle('flux-st__pill--checked', i === checked);
      });
    }
  }

  /**
   * Modal cropper met FRAME-based crop.
   * - Image dimensies 100% via JS (geen min-* CSS)
   * - Bij init: scale = 1, image cover-vult het frame
   * - Zoom range: 0.3 (heel uitgezoomed, zwarte ruimte rond) tot 5 (sterk ingezoomd)
   * - Drag = vrij bewegen, ook bij scale < 1
   */
  class Cropper {
    constructor(container, img) {
      this.container = container;
      this.img = img;
      // baseScale = scale waarop image precies het FRAME cover-vult
      const cw = container.clientWidth;
      const frameSize = cw * 0.84;
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      this.baseScale = Math.max(frameSize / iw, frameSize / ih);
      this.scale = 1; // multiplier op baseScale
      this.offsetX = 0;
      this.offsetY = 0;
      this.dragging = false;
      this.bind();
      this.render();
    }
    bind() {
      const c = this.container;
      this._down = (e) => this.onDown(e);
      this._move = (e) => this.onMove(e);
      this._up = () => this.onUp();
      c.addEventListener('pointerdown', this._down);
      window.addEventListener('pointermove', this._move);
      window.addEventListener('pointerup', this._up);
      this._wheel = (e) => { e.preventDefault(); this.zoom(e.deltaY < 0 ? 1.1 : 1 / 1.1); };
      c.addEventListener('wheel', this._wheel, { passive: false });
    }
    onDown(e) {
      this.dragging = true;
      this.startX = e.clientX;
      this.startY = e.clientY;
      this.startTx = this.tx;
      this.startTy = this.ty;
      try { this.container.setPointerCapture?.(e.pointerId); } catch {}
    }
    onMove(e) {
      if (!this.dragging) return;
      const dx = e.clientX - this.startX;
      const dy = e.clientY - this.startY;
      this.tx = this.startTx + dx;
      this.ty = this.startTy + dy;
      this.applyTransform();
    }
    onUp() { this.dragging = false; }
    setScale(s) {
      const newScale = clamp(s, 0.3, 5);
      // Behoud center punt — anders verspringt image bij zoom
      const cw = this.container.clientWidth;
      const cx = cw / 2;
      const oldEff = this.baseScale * this.scale;
      const newEff = this.baseScale * newScale;
      // huidige center in image coords
      const imgCenterX = (cx - this.tx) / oldEff;
      const imgCenterY = (cx - this.ty) / oldEff;
      this.scale = newScale;
      // re-anchor
      this.tx = cx - imgCenterX * newEff;
      this.ty = cx - imgCenterY * newEff;
      this.applyTransform();
    }
    zoom(factor) { this.setScale(this.scale * factor); }
    render() {
      const cw = this.container.clientWidth;
      const iw = this.img.naturalWidth;
      const ih = this.img.naturalHeight;
      if (!cw || !iw) return;
      const eff = this.baseScale * this.scale;
      const dispW = iw * eff;
      const dispH = ih * eff;
      // Initial center op midden van container
      this.tx = (cw - dispW) / 2;
      this.ty = (cw - dispH) / 2;
      this.applyTransform();
    }
    applyTransform() {
      const eff = this.baseScale * this.scale;
      const dispW = this.img.naturalWidth * eff;
      const dispH = this.img.naturalHeight * eff;
      const img = this.img;
      img.style.width = dispW + 'px';
      img.style.height = dispH + 'px';
      img.style.top = '0';
      img.style.left = '0';
      img.style.transform = `translate(${this.tx}px, ${this.ty}px)`;
    }
    /**
     * Render het FRAME (het witte vierkant in het midden) naar canvas → base64.
     */
    toDataURL(targetSize, quality = 0.95) {
      return new Promise((resolve, reject) => {
        try {
          const cw = this.container.clientWidth;
          const frameSize = cw * 0.84;
          const frameLeft = cw * 0.08;
          const frameTop = cw * 0.08;
          const eff = this.baseScale * this.scale;
          // Frame top-left in image coords (native pixels)
          const sx = (frameLeft - this.tx) / eff;
          const sy = (frameTop - this.ty) / eff;
          const sw = frameSize / eff;
          const sh = frameSize / eff;
          const canvas = document.createElement('canvas');
          canvas.width = targetSize;
          canvas.height = targetSize;
          const ctx = canvas.getContext('2d');
          ctx.imageSmoothingQuality = 'high';
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, targetSize, targetSize);
          // Image kan deels buiten frame zijn (witte ruimte zichtbaar bij zoom <1)
          // drawImage clipt automatisch source dat buiten image valt
          ctx.drawImage(this.img, sx, sy, sw, sh, 0, 0, targetSize, targetSize);
          resolve(canvas.toDataURL('image/jpeg', quality));
        } catch (e) { reject(e); }
      });
    }
  }

  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

  function loadImage(imgElement, src) {
    return new Promise((resolve, reject) => {
      const tester = new Image();
      tester.onload = () => { imgElement.src = src; requestAnimationFrame(() => resolve()); };
      tester.onerror = () => reject(new Error('Kon resultaat-afbeelding niet laden'));
      tester.src = src;
    });
  }

  function resizeImageBlob(file, maxMp) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        const mp = (img.width * img.height) / 1_000_000;
        let { width, height } = img;
        if (mp > maxMp) {
          const scale = Math.sqrt(maxMp / mp);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (!blob) return reject(new Error('Blob conversie mislukt'));
          resolve({ blob, width, height });
        }, 'image/jpeg', 0.95);
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Kon afbeelding niet decoderen')); };
      img.src = url;
    });
  }
})();
